/**
 * The platform administrator's domain vocabulary.
 *
 * These enums are a cross-service contract, mirrored from
 * campusone-core-service/src/platform-admin/domain/contracts/*.ts. They are
 * duplicated rather than imported because the browser cannot import from a
 * NestJS service, and they are written as `as const` arrays so the same list can
 * drive both the type and the filter controls without a second, drifting copy.
 *
 * Beware: three incompatible InstitutionType enums exist across the estate today.
 * This file follows the nine-value platform contract, which is the one the
 * platform-admin endpoints are being written against. The legacy identity-service
 * onboarding endpoint still validates against its own eight-value list, so the
 * The onboarding-case form keeps its own narrower creation set and the backend
 * supplies the institution code during case creation.
 */

export const ONBOARDING_STATES = [
  'DRAFT',
  'INFORMATION_REQUIRED',
  'DOCUMENTS_PENDING',
  'UNDER_REVIEW',
  'COMPLIANCE_REVIEW',
  'TECHNICAL_CONFIGURATION',
  'PRODUCT_CONFIGURATION',
  'ADMIN_INVITATION_PENDING',
  'READY_FOR_ACTIVATION',
  'ACTIVE',
  'REJECTED',
  'SUSPENDED',
  'CLOSED',
] as const;

export type OnboardingState = (typeof ONBOARDING_STATES)[number];

/** States a case can sit in without anyone being expected to act. */
export const TERMINAL_ONBOARDING_STATES: readonly OnboardingState[] = [
  'ACTIVE',
  'REJECTED',
  'CLOSED',
];

/**
 * The legal case moves, mirrored exactly from ONBOARDING_TRANSITIONS in core.
 *
 * The console previously guessed at this map and the guess was wrong in both
 * directions: it offered DRAFT from nowhere after a rejection (core allows
 * REJECTED → DRAFT, so a returning prospect keeps its reserved code) and it
 * offered TECHNICAL_CONFIGURATION → INFORMATION_REQUIRED, which core refuses. A
 * button that produces a 409 teaches an operator the rules are arbitrary. Core is
 * still the enforcer; this map only decides which buttons are worth drawing.
 */
export const ONBOARDING_TRANSITIONS: Readonly<Record<OnboardingState, readonly OnboardingState[]>> =
  {
    DRAFT: ['INFORMATION_REQUIRED', 'DOCUMENTS_PENDING', 'UNDER_REVIEW', 'REJECTED', 'CLOSED'],
    INFORMATION_REQUIRED: ['DRAFT', 'DOCUMENTS_PENDING', 'UNDER_REVIEW', 'REJECTED', 'CLOSED'],
    DOCUMENTS_PENDING: ['INFORMATION_REQUIRED', 'UNDER_REVIEW', 'REJECTED', 'CLOSED'],
    UNDER_REVIEW: [
      'INFORMATION_REQUIRED',
      'DOCUMENTS_PENDING',
      'COMPLIANCE_REVIEW',
      'REJECTED',
      'CLOSED',
    ],
    COMPLIANCE_REVIEW: [
      'INFORMATION_REQUIRED',
      'DOCUMENTS_PENDING',
      'TECHNICAL_CONFIGURATION',
      'REJECTED',
      'CLOSED',
    ],
    TECHNICAL_CONFIGURATION: [
      'COMPLIANCE_REVIEW',
      'PRODUCT_CONFIGURATION',
      'REJECTED',
      'CLOSED',
    ],
    PRODUCT_CONFIGURATION: [
      'TECHNICAL_CONFIGURATION',
      'ADMIN_INVITATION_PENDING',
      'REJECTED',
      'CLOSED',
    ],
    ADMIN_INVITATION_PENDING: [
      'PRODUCT_CONFIGURATION',
      'READY_FOR_ACTIVATION',
      'REJECTED',
      'CLOSED',
    ],
    READY_FOR_ACTIVATION: ['ADMIN_INVITATION_PENDING', 'ACTIVE', 'REJECTED', 'CLOSED'],
    ACTIVE: ['SUSPENDED', 'CLOSED'],
    SUSPENDED: ['ACTIVE', 'CLOSED'],
    REJECTED: ['DRAFT', 'CLOSED'],
    CLOSED: [],
  };

/** What GET /onboarding/cases/:id/readiness answers before a move is attempted. */
export interface OnboardingReadiness {
  currentState: OnboardingState;
  targetState: OnboardingState;
  transitionAllowed: boolean;
  reasonRequired: boolean;
  incompleteStages: string[];
  blockingActions: { id: string; title: string }[];
}

export const INSTITUTION_TYPES = [
  'PRIMARY_SCHOOL',
  'SECONDARY_SCHOOL',
  'COMBINED_SCHOOL',
  'UNIVERSITY',
  'HIGHER_EDUCATION_INSTITUTION',
  'TRAINING_INSTITUTION',
  'VOCATIONAL_INSTITUTION',
  'SCHOOL_GROUP',
  'EDUCATION_AUTHORITY',
] as const;

export type InstitutionType = (typeof INSTITUTION_TYPES)[number];

/**
 * Types that existed before the nine-value contract was agreed and are no longer
 * accepted on any write path.
 *
 * They are still in the database — see RETIRED_INSTITUTION_TYPES in
 * campusone-identity-service. None of the nine contract types means "a microfinance
 * institution", so nothing was remapped and the column is now NULLABLE for those
 * rows. That is why `institutionType` is `InstitutionType | null` throughout this
 * file: a retired row arrives as null, and rendering it as a blank cell would read
 * as missing data while defaulting it to a plausible type would be a lie recorded
 * on screen. It gets its own label.
 */
export const RETIRED_INSTITUTION_TYPES = ['MFI', 'BANK', 'MERCHANT_ORGANIZATION', 'CSKY_INTERNAL'] as const;

/** What the server may send in an institutionType field, including "no longer classified". */
export type NullableInstitutionType = InstitutionType | null;

export const PRODUCT_STATES = [
  'NOT_SUBSCRIBED',
  'PENDING_CONFIGURATION',
  'CONFIGURING',
  'ENABLED',
  'SUSPENDED',
  'DISABLED',
  'EXPIRED',
] as const;

export type ProductState = (typeof PRODUCT_STATES)[number];

/** Mirrors PRODUCT_STATES_GRANTING_ACCESS in core. Only ENABLED lets anyone in. */
export function productStateGrantsAccess(state: ProductState): boolean {
  return state === 'ENABLED';
}

/**
 * A product activation that started and never finished.
 *
 * Used to answer "which activations are incomplete?" without a second endpoint:
 * these are the states where CSKY, not the institution, still owes work.
 */
export function productStateIsIncomplete(state: ProductState): boolean {
  return state === 'PENDING_CONFIGURATION' || state === 'CONFIGURING';
}

export const INTEGRATION_STATES = [
  'NOT_CONFIGURED',
  'CONFIGURING',
  'HEALTHY',
  'DEGRADED',
  'FAILED',
  'DISABLED',
] as const;

export type IntegrationState = (typeof INTEGRATION_STATES)[number];

export const SUSPENSION_SCOPES = [
  'FULL_PLATFORM',
  'AUTHENTICATION_ONLY',
  'SPECIFIC_PRODUCT',
  'PAYMENT_ACCESS',
  'MARKET_ACCESS',
  'LMS_ACCESS',
  'API_ACCESS',
] as const;

export type SuspensionScope = (typeof SUSPENSION_SCOPES)[number];

export const INVITATION_STATUSES = [
  'PENDING',
  'ACCEPTED',
  'EXPIRED',
  'REVOKED',
  'CANCELLED',
  'FAILED',
] as const;

export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const;
export type Severity = (typeof SEVERITIES)[number];

/* ─── PA-01 dashboard ─── */

export interface InstitutionCounts {
  total: number;
  active: number;
  awaitingOnboarding: number;
  suspended: number;
}

export interface InstitutionTypeCount {
  institutionType: NullableInstitutionType;
  count: number;
}

/** An institution the administrator is expected to do something about. */
export interface AttentionItem {
  institutionId: string;
  institutionName: string;
  institutionCode: string;
  institutionType: NullableInstitutionType;
  onboardingState: OnboardingState;
  /** Why it surfaced, already resolved server-side; free text, shown verbatim. */
  reason: string;
  severity: Severity;
  since: string;
  /** Present when the item traces back to an onboarding case. */
  caseId?: string;
}

export interface IntegrationHealth {
  id: string;
  name: string;
  institutionId?: string;
  institutionName?: string;
  state: IntegrationState;
  lastCheckedAt?: string;
  lastError?: string;
  consecutiveFailures?: number;
}

export interface ProductActivationSummary {
  institutionId: string;
  institutionName: string;
  productCode: string;
  productName: string;
  state: ProductState;
  /** What is still outstanding, resolved server-side. */
  blockedOn?: string;
  since?: string;
}

export interface InvitationFailure {
  invitationId: string;
  email: string;
  institutionId: string;
  institutionName: string;
  status: InvitationStatus;
  failedAt?: string;
  reason?: string;
}

export interface ServiceHealth {
  name: string;
  state: IntegrationState;
  latencyMs?: number;
  checkedAt?: string;
  detail?: string;
}

/** An entry from the privileged-action audit trail. */
export interface PrivilegedChange {
  id: string;
  action: string;
  actor: string;
  institutionId?: string;
  institutionName?: string;
  occurredAt: string;
  reason?: string;
  severity: Severity;
}

export interface OnboardingTask {
  caseId: string;
  institutionName: string;
  state: OnboardingState;
  label: string;
  dueAt?: string;
  overdue?: boolean;
  assignedTo?: string;
}

export interface PlatformAlert {
  id: string;
  title: string;
  detail?: string;
  severity: Severity;
  raisedAt: string;
  institutionId?: string;
  institutionName?: string;
}

/**
 * Every section is optional on purpose.
 *
 * The backing services are being built section by section, so a dashboard that
 * required all of them would show nothing until the last one landed. Each panel
 * decides for itself whether it has data, and says so when it does not.
 */
export interface PlatformDashboard {
  counts?: InstitutionCounts;
  byType?: InstitutionTypeCount[];
  needsAttention?: AttentionItem[];
  integrations?: IntegrationHealth[];
  productActivations?: ProductActivationSummary[];
  invitationFailures?: InvitationFailure[];
  services?: ServiceHealth[];
  privilegedChanges?: PrivilegedChange[];
  onboardingTasks?: OnboardingTask[];
  alerts?: PlatformAlert[];
  generatedAt?: string;
}

/* ─── PA-02 onboarding cases ─── */

/**
 * One onboarding case, in core's field names.
 *
 * `legalName` and `caseReference` are what OnboardingCase stores. This file used
 * to call them `institutionName` and `reference`, and because the list normaliser
 * required `institutionName` to be present, EVERY case core returned was filtered
 * out and the screen reported an empty pipeline. That is the most expensive kind
 * of drift: no error, no 404, just nothing there.
 */
export interface OnboardingCaseSummary {
  id: string;
  caseReference: string;
  legalName: string;
  displayName?: string | null;
  institutionCode: string;
  institutionType: NullableInstitutionType;
  state: OnboardingState;
  countryCode?: string;
  createdAt: string;
  updatedAt: string;
  onboardingOwnerId?: string;
  onboardingOwnerName?: string | null;
  contactEmail?: string;
  primaryContactName?: string;
  targetActivationDate?: string | null;
  /** Set once the case has produced an institution. */
  institutionId?: string | null;
  rejectionReason?: string | null;
  metadata?: Record<string, unknown> | null;
}

/** What to put on screen for a case that stores two names. */
export function caseDisplayName(summary: OnboardingCaseSummary): string {
  return summary.displayName || summary.legalName;
}

export const CHECKLIST_STAGES = [
  'INFORMATION',
  'DOCUMENTS',
  'COMPLIANCE',
  'TECHNICAL',
  'PRODUCT',
  'ADMIN_HANDOVER',
] as const;

export type ChecklistStage = (typeof CHECKLIST_STAGES)[number];

/**
 * A checklist item, keyed by `id` for writes and `code` for meaning.
 *
 * Ticking one is `PATCH .../checklist/:itemId` with the row's UUID — not a POST
 * with the item's code, which is what this console sent and what produced a 404
 * on every tick.
 */
export interface OnboardingChecklistItem {
  id: string;
  code: string;
  label: string;
  stage: ChecklistStage;
  completed: boolean;
  required: boolean;
  completedAt?: string | null;
  completedBy?: string | null;
  notes?: string | null;
  displayOrder?: number;
}

export interface OnboardingHistoryEntry {
  id: string;
  fromState?: OnboardingState | null;
  toState: OnboardingState;
  actorId?: string;
  actorName?: string | null;
  occurredAt: string;
  reason?: string | null;
  note?: string | null;
}

export const ONBOARDING_ACTION_STATUSES = ['OPEN', 'RESOLVED', 'CANCELLED'] as const;
export type OnboardingActionStatus = (typeof ONBOARDING_ACTION_STATUSES)[number];

/** An OnboardingAction row: a thing somebody owes before the case can move. */
export interface OnboardingOutstandingAction {
  id: string;
  title: string;
  description?: string | null;
  assignedToId?: string | null;
  assignedToName?: string | null;
  dueDate?: string | null;
  status: OnboardingActionStatus;
  /** True when the case cannot progress until this is resolved. */
  blocksTransition: boolean;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolutionNote?: string | null;
}

export interface OnboardingCaseDetail extends OnboardingCaseSummary {
  checklist?: OnboardingChecklistItem[];
  history?: OnboardingHistoryEntry[];
  /** Core names this relation `actions`. */
  actions?: OnboardingOutstandingAction[];
}

export interface CreateOnboardingCaseRequest {
  institutionName: string;
  countryCode: string;
  institutionType: InstitutionType;
  contactEmail: string;
  contactName?: string;
  externalReference?: string;
  notes?: string;
}

/**
 * `context`, not `note` — the DTO validates with `forbidNonWhitelisted`, so an
 * unrecognised field is a 400 rather than a quietly dropped one.
 */
export interface TransitionOnboardingCaseRequest {
  targetState: OnboardingState;
  reason: string;
  context?: Record<string, unknown>;
}

/* ─── Institutions, products, lifecycle ─── */

export const INSTITUTION_STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'PROVISIONING'] as const;
export type InstitutionStatus = (typeof INSTITUTION_STATUSES)[number];

/**
 * An institution as core's `platform_institutions` table actually holds it.
 *
 * Mirrored field for field from PlatformInstitution. There is no `name`: core
 * stores `legalName` and `displayName` separately, and collapsing them here would
 * mean the console silently chose one and could not say which. There is no
 * `onboardingState` either — that belongs to the onboarding CASE, and an
 * institution can outlive the case that created it.
 */
/**
 * An institution as the DIRECTORY serves it — `PlatformInstitutionSummaryDto` and
 * `PlatformInstitutionDetailDto` in campusone-core-service.
 *
 * THIS USED TO BE MODELLED ON THE TABLE, NOT ON THE ENDPOINT, and every field that
 * differed broke something. `platform_institutions` has `legalName`, `displayName`
 * and `status`; the directory sends `name` — the display name where one is set, the
 * legal name otherwise — and `onboardingState`. It sends no `legalName` at all.
 *
 * The two failures that caused, both silent in their own way:
 *   - `normalizeInstitutionDetail` required `legalName`, so it threw "the server
 *     returned an institution this app cannot read" on a response that was perfectly
 *     well-formed. Because a plain Error carries no status, the screen reported it as
 *     "could not reach the service" — pointing at the network for a parse fault.
 *   - `normalizeInstitutionList` listed `legalName` among its required keys, so
 *     `rowsWith` discarded EVERY row and the register read empty with no error at all.
 *
 * The same mistake, with the same two symptoms, is recorded at the top of
 * OnboardingCaseDetailPage for the cases pipeline. Model these on what the endpoint
 * sends, and check the DTO rather than the entity when adding a field.
 */
export interface PlatformInstitutionRecord {
  id: string;
  tenantId: string;
  code: string;
  /** Display name where one is set, legal name otherwise. Never empty. */
  name: string;
  institutionType: NullableInstitutionType;
  /**
   * The onboarding state, NOT the institution's `status` column. The directory
   * reports where the institution stands in its lifecycle, which is the question the
   * register is read to answer.
   */
  onboardingState: OnboardingState;
  createdAt?: string;
  /** Activations in ENABLED. */
  enabledProductCount?: number;
  /** Activations started and never finished — the working list's whole point. */
  incompleteProductCount?: number;
  /** Present only while actually suspended. */
  suspendedAt?: string;

  // Detail only, absent from a list row.
  countryCode?: string;
  timezone?: string;
  /** Recorded on the onboarding case that produced this institution; historical. */
  contactEmail?: string;
  suspensionReason?: string;
  suspensionScope?: string;
}

/**
 * What to put on screen. Core has already resolved display-versus-legal, so there is
 * no longer a choice to make here — kept as a function so call sites do not have to
 * change again if that ever stops being true.
 */
export function institutionDisplayName(institution: PlatformInstitutionRecord): string {
  return institution.name;
}

/**
 * One institution's subscription to one product, as ProductActivation stores it.
 *
 * `isTrial`, not `trial`, and `lastStateChangedBy`, not `lastChangedBy` — the
 * previous names were invented here and matched nothing the server sends, so the
 * trial tag never appeared and the "changed by" column was permanently blank.
 */
/**
 * One product as the platform catalogue defines it, independent of any institution.
 *
 * A HAND-WRITTEN READING TYPE, not the server entity. The endpoint returns the
 * ProductDefinition row whole — including a free-form `metadata` blob — and typing the
 * client against that would make every column the server happens to have into something
 * this app claims to understand. Only the fields the picker actually shows are declared.
 */
export interface ProductCatalogueEntry {
  code: string;
  name: string;
  description?: string | null;
  /** ACTIVE, INACTIVE, DEPRECATED or COMING_SOON. Governs whether it may be sold today. */
  status?: string | null;
  category?: string | null;
  displayOrder?: number | null;
  /** The product's own web origin, used to prefill the launch field. */
  launchUrl?: string | null;
}

export interface InstitutionProductAssignment {
  id: string;
  institutionId: string;
  productCode: string;
  state: ProductState;
  startsAt?: string | null;
  expiresAt?: string | null;
  isTrial: boolean;
  trialEndsAt?: string | null;
  configurationStatus: ProductConfigurationStatus;
  configurationNotes?: string | null;
  launchUrl?: string | null;
  /** Product codes that must be ENABLED before this one may be. */
  dependencies?: string[];
  configuration?: Record<string, unknown> | null;
  enabledAt?: string | null;
  disabledAt?: string | null;
  lastStateReason?: string | null;
  lastStateChangedAt?: string | null;
  lastStateChangedBy?: string | null;
  /**
   * The actor's name, resolved by core from the subject above.
   *
   * Null when it could not be resolved — a service account, a person since removed, or
   * identity being unreachable. Null and "unknown" are the same to a reader here, but they
   * must not be confused with the id itself: core never echoes the id back as a name, so
   * the console can render "we could not look this up" differently from a real name.
   */
  lastStateChangedByName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const PRODUCT_CONFIGURATION_STATUSES = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'COMPLETE',
  'BLOCKED',
] as const;

export type ProductConfigurationStatus = (typeof PRODUCT_CONFIGURATION_STATUSES)[number];

/**
 * The legal product-state moves, mirrored from PRODUCT_STATE_TRANSITIONS in core.
 *
 * The console used to offer "activate" from NOT_SUBSCRIBED, DISABLED and
 * PENDING_CONFIGURATION, none of which core will accept: a product reaches ENABLED
 * only from CONFIGURING or SUSPENDED. Offering the button anyway taught operators
 * that the rules were arbitrary, because the refusal arrived after they had already
 * decided. The map is repeated here so the buttons on screen are the moves that
 * exist; the server is still the one that enforces it.
 */
export const PRODUCT_STATE_TRANSITIONS: Readonly<Record<ProductState, readonly ProductState[]>> = {
  NOT_SUBSCRIBED: ['PENDING_CONFIGURATION'],
  PENDING_CONFIGURATION: ['CONFIGURING', 'DISABLED', 'NOT_SUBSCRIBED'],
  CONFIGURING: ['ENABLED', 'PENDING_CONFIGURATION', 'DISABLED', 'NOT_SUBSCRIBED'],
  ENABLED: ['CONFIGURING', 'SUSPENDED', 'DISABLED', 'EXPIRED'],
  SUSPENDED: ['ENABLED', 'DISABLED', 'EXPIRED'],
  DISABLED: ['PENDING_CONFIGURATION', 'NOT_SUBSCRIBED'],
  EXPIRED: ['PENDING_CONFIGURATION', 'DISABLED', 'NOT_SUBSCRIBED'],
};

/** Target states core refuses without a reason, because each takes access away. */
export const PRODUCT_STATES_REQUIRING_REASON: readonly ProductState[] = [
  'SUSPENDED',
  'DISABLED',
  'EXPIRED',
  'NOT_SUBSCRIBED',
];

/** POST /platform/institutions/:id/products — the first subscription. */
export interface SubscribeProductRequest {
  productCode: string;
  startsAt?: string;
  expiresAt?: string;
  isTrial?: boolean;
  trialEndsAt?: string;
  launchUrl?: string;
  configuration?: Record<string, unknown>;
}

/**
 * POST /platform/institutions/:id/products/:code/transitions.
 *
 * One endpoint with a target state, rather than an /activate, /suspend and
 * /disable verb apiece. The console asked for the three verbs and got three 404s;
 * the transition is also the better contract, because the set of reachable states
 * is a property of the state machine and not of how many routes somebody wrote.
 */
export interface TransitionProductStateRequest {
  targetState: ProductState;
  reason?: string;
}

/* ─── PA-08 institution lifecycle ─── */

export const INSTITUTION_LIFECYCLE_STATES = [
  'PENDING_ACTIVATION',
  'ACTIVE',
  'SUSPENDED',
  'MAINTENANCE',
  'CLOSED',
  'ARCHIVED',
] as const;

export type InstitutionLifecycleState = (typeof INSTITUTION_LIFECYCLE_STATES)[number];

/**
 * Mirrored from InstitutionLifecycleAction in core.
 *
 * The console previously offered RESUME, which core has never had a name for —
 * the action is REACTIVATE, and the request was rejected. ENTER_MAINTENANCE,
 * EXIT_MAINTENANCE and ARCHIVE existed on the server and were unreachable here.
 */
export const LIFECYCLE_ACTIONS = [
  'ACTIVATE',
  'SUSPEND',
  'REACTIVATE',
  'ENTER_MAINTENANCE',
  'EXIT_MAINTENANCE',
  'CLOSE',
  'ARCHIVE',
] as const;

export type LifecycleAction = (typeof LIFECYCLE_ACTIONS)[number];

export interface InstitutionLifecycle {
  id: string;
  institutionId: string;
  state: InstitutionLifecycleState;
  suspensionScope: SuspensionScope | null;
  suspendedProductCode: string | null;
  currentReason: string | null;
  stateChangedAt?: string | null;
  stateChangedBy?: string | null;
  maintenanceEndsAt?: string | null;
}

export interface InstitutionLifecycleEvent {
  id: string;
  institutionId: string;
  action: LifecycleAction;
  fromState: InstitutionLifecycleState;
  toState: InstitutionLifecycleState;
  suspensionScope: SuspensionScope | null;
  suspendedProductCode: string | null;
  reason: string;
  impactSummary?: Record<string, unknown>;
  actorId: string;
  actorName?: string | null;
  occurredAt: string;
}

/**
 * What the action will do, fetched before it is confirmed.
 *
 * The point of PA-08 is that nobody suspends a university by clicking once. The
 * server computes the blast radius because only the server knows what sits behind
 * an institution id.
 *
 * These are the fields LifecycleImpactSummary actually carries. The previous
 * interface promised `affectedUsers`, `consequences`, `reversible` and
 * `dataRetained`, none of which core computes — so the dialog rendered a blank
 * user count and an unconditional "data is kept" next to a suspension, which is a
 * claim this console was in no position to make.
 */
export interface LifecycleImpact {
  institutionId: string;
  action: LifecycleAction;
  currentState: InstitutionLifecycleState;
  resultingState: InstitutionLifecycleState;
  suspensionScope: SuspensionScope | null;
  productsLosingAccess: string[];
  productsRetainingAccess: string[];
  enabledFeatureFlagCount: number;
  campusCount: number;
  branchCount: number;
  liveIntegrationCount: number;
  warnings: string[];
}

export interface LifecycleActionRequest {
  action: LifecycleAction;
  reason: string;
  /** Required for SUSPEND, ignored by every other action. */
  suspensionScope?: SuspensionScope;
  /** Required when suspensionScope is SPECIFIC_PRODUCT. */
  suspendedProductCode?: string;
  /** Optional for ENTER_MAINTENANCE. */
  maintenanceEndsAt?: string;
}

/** What POST /lifecycle/actions returns: the new state and the record of the decision. */
export interface LifecycleActionResult {
  lifecycle: InstitutionLifecycle;
  event: InstitutionLifecycleEvent;
}

/* ─── PA-07 administrator invitations ─── */

/**
 * How the invited administrator is meant to hold authority at the institution.
 *
 * Mirrored from InvitationType in campusone-identity-service. PRIMARY and
 * SECONDARY are full administrators; the difference is that an institution may
 * have exactly one live PRIMARY (a pending one counts as live). STANDARD is a
 * fixed-term grant accepted through the standard invitation flow, and the
 * administrator listing deliberately excludes it, so this console only offers
 * the first two.
 */
export const INVITATION_TYPES = [
  'PRIMARY_ADMINISTRATOR',
  'SECONDARY_ADMINISTRATOR',
  'STANDARD',
] as const;

export type InvitationType = (typeof INVITATION_TYPES)[number];

/**
 * The contract role names the identity service accepts for institution
 * administrators, and the catalogue roles they resolve to.
 *
 * These are the keys of INSTITUTIONAL_ADMIN_ROLE_CODES in
 * campusone-identity-service — contract names, not catalogue codes. The
 * service validates the request against this allow-list, so the console must
 * offer exactly these names; what each resolves to (SCHOOL_ADMIN becomes
 * PORTAL_ADMINISTRATOR, for example) is the service's decision, not this
 * app's.
 */
export const ADMIN_ROLE_CODES = [
  'SCHOOL_ADMIN',
  'PRINCIPAL',
  'REGISTRAR',
  'SCHOOL_BURSAR',
  'ACADEMIC_DIRECTOR',
] as const;

/**
 * The contract role names the identity service accepts for GROUP administrators,
 * and the catalogue roles they resolve to.
 *
 * Same arrangement as ADMIN_ROLE_CODES, for the group-scoped invitation surface.
 * The identities Phase E (group contexts) and this phase (group administrator
 * invitations) add in parallel accept these names on InviteAdministratorDto —
 * which is growing an optional groupId rather than gaining a second endpoint —
 * so the console offers exactly this allow-list and no other.
 */
/**
 * Seats that may administer a group from CampusOne.
 *
 * Blueprint family mapping (Wave 2 — group executives):
 * - G01 Group President / Founder → GROUP_PRESIDENT (all KPIs incl. finance)
 * - G02 Group DG / Executive Director → no distinct catalogue role yet; closest is
 *   GROUP_ADMINISTRATOR for ops without money, or PRESIDENT when finance is granted
 * - G03 Group Academic Director → GROUP_ACADEMIC_DIRECTOR
 * - G04 Group Finance Director → GROUP_FINANCE_MANAGER
 * - G05 Group HR Director → no catalogue role / no cross-institution HR API yet
 * - G06 Group System Admin → GROUP_ADMINISTRATOR
 *
 * GROUP_AUDITOR is analytics/audit without a G0x card of its own.
 */
export const GROUP_ADMIN_ROLE_CODES = [
  'GROUP_ADMINISTRATOR',
  'GROUP_PRESIDENT',
  'GROUP_FINANCE_MANAGER',
  'GROUP_ACADEMIC_DIRECTOR',
  'GROUP_AUDITOR',
] as const;

export type GroupAdminRoleCode = (typeof GROUP_ADMIN_ROLE_CODES)[number];

export interface AdministratorInvitation {
  id: string;
  email: string;
  invitationType: InvitationType;
  initialRoleNames: string[];
  status: InvitationStatus;
  expiresAt?: string;
  acceptedAt?: string;
  resendCount?: number;
  lastSentAt?: string;
  /**
   * Present when the invitation is scoped to a group rather than to an
   * institution. The institution fields are then absent — the invitation is
   * not "for" any single member, so the group administrators panel lists it
   * under the group instead.
   */
  groupId?: string;
  groupName?: string;
  groupType?: string;
}

export interface InviteAdministratorRequest {
  email: string;
  firstName: string;
  lastName: string;
  invitationType: InvitationType;
  initialRoleNames: string[];
  phone?: string;
  /**
   * The group the invitation is scoped to. Institution invitations omit it;
   * the payload then carries an institutionId instead, and identity decides
   * what the invitation is for from whichever of the two is present.
   */
  groupId?: string;
}

export interface InstitutionAdministratorsResponse {
  invitations: AdministratorInvitation[];
}

/* ─── Paging and sorting ─── */

/**
 * A page of results, in the shape campusone-identity-service's `PaginatedResult`
 * already uses.
 *
 * The console had no paging at all, and the cost was not cosmetic: a client-side
 * "open cases" fold over whatever the first response happened to contain reports a
 * count that is right until the list outgrows one page and then quietly wrong
 * forever. Everything that can grow now carries a server-side total.
 */
export interface Page<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type SortDirection = 'ASC' | 'DESC';

export interface PageRequest {
  page?: number;
  limit?: number;
  sort?: string;
  order?: SortDirection;
}

export const DEFAULT_PAGE_SIZE = 25;

/* ─── PA-14 audit and privileged-action reporting ─── */

export const AUDIT_OUTCOMES = ['SUCCESS', 'FAILURE', 'DENIED', 'ERROR'] as const;
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

/**
 * The privileged actions the audit trail can report on.
 *
 * Mirrored from PrivilegedActionType in campusone-identity-service. These are the
 * wire values — dotted and lower-case — and they are listed rather than derived so
 * that the filter control cannot offer an action type the server has never heard
 * of. A filter that silently matches nothing returns an empty report, and an empty
 * report reads exactly like a clean bill of health.
 */
export const PRIVILEGED_ACTION_TYPES = [
  'platform.administrator.invited',
  'platform.administrator.invitation-resent',
  'platform.administrator.invitation-revoked',
  'platform.administrator.invitation-accepted',
  'platform.identity.linked',
  'platform.role.elevated',
  'platform.role.revoked',
  'platform.account.suspended',
  'platform.account.reinstated',
  'platform.product.activated',
  'platform.product.deactivated',
  'platform.mapping.changed',
  'platform.delegation.granted',
  'platform.delegation.approved',
  'platform.delegation.revoked',
  'platform.delegation.escalation-blocked',
  'platform.access.cross-tenant-denied',
  'platform.access.failed',
  'platform.support-access.requested',
  'platform.support-access.approved',
  'platform.support-access.denied',
  'platform.support-access.started',
  'platform.support-access.action',
  'platform.support-access.ended',
  'platform.support-access.expired',
] as const;

export type PrivilegedActionType = (typeof PRIVILEGED_ACTION_TYPES)[number];

export interface ApprovalChainEntry {
  step: string;
  actorId: string;
  actorType?: 'PERSON' | 'KEYCLOAK_SUBJECT' | 'SYSTEM';
  decision: 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'EXECUTED' | 'REVOKED';
  occurredAt: string;
  note?: string;
}

/**
 * One recorded privileged action.
 *
 * `beforeValue` and `afterValue` are whole snapshots rather than a diff, because
 * these rows outlive several schema versions and a diff is only meaningful against
 * a shape that has not changed since. The comparison is therefore computed at read
 * time, and the screen says which keys appear on only one side rather than
 * pretending the two snapshots are the same shape.
 */
export interface AuditEvent {
  id: string;
  eventType: string;
  actorPersonId?: string;
  actorKeycloakUserId?: string;
  /** Resolved server-side where the person still exists. Ids are the source of truth. */
  actorName?: string;
  targetType: string;
  targetId?: string;
  tenantId?: string;
  institutionId?: string;
  institutionName?: string;
  correlationId: string;
  requestId?: string;
  sourceIp?: string;
  userAgent?: string;
  outcome: AuditOutcome;
  reason?: string;
  metadata?: Record<string, unknown>;
  beforeValue?: Record<string, unknown>;
  afterValue?: Record<string, unknown>;
  approvalChain?: ApprovalChainEntry[];
  occurredAt: string;
}

/**
 * Core's own governance record, which is NOT identity's AuditEvent.
 *
 * Different service, different table, different field names: `action` rather than
 * `eventType`, `resourceType`/`resourceId` rather than `targetType`/`targetId`,
 * `beforeState`/`afterState` rather than `beforeValue`/`afterValue`, and no
 * outcome at all — a row exists only because the action happened, written inside
 * the same transaction. Both were typed as one interface here, which would have
 * rendered a core row with four blank columns and an outcome it never carried.
 */
export interface PlatformAuditRecord {
  id: string;
  action: string;
  resourceType: string;
  resourceId: string;
  institutionId?: string | null;
  actorId: string;
  actorName?: string | null;
  reason?: string | null;
  beforeState?: Record<string, unknown> | null;
  afterState?: Record<string, unknown> | null;
  correlationId?: string | null;
  occurredAt: string;
}

export interface AuditSearchFilters extends PageRequest {
  actorPersonId?: string;
  actorKeycloakUserId?: string;
  institutionId?: string;
  tenantId?: string;
  eventTypes?: readonly string[];
  targetType?: string;
  targetId?: string;
  outcomes?: readonly AuditOutcome[];
  from?: string;
  to?: string;
  correlationId?: string;
}

/**
 * The dedicated views the audit API exposes as separate routes.
 *
 * These are not a filter the client applies to one endpoint. The server owns which
 * event types constitute a role elevation, and a client-side approximation would
 * drift the moment a new type is added — producing a report that omits half of what
 * it claims to cover while looking perfectly healthy.
 */
export const AUDIT_VIEWS = [
  'EVENTS',
  'DENIALS',
  'CROSS_TENANT',
  'ROLE_ELEVATIONS',
  'SUSPENSIONS',
  'PRODUCT_CHANGES',
] as const;

export type AuditView = (typeof AUDIT_VIEWS)[number];

/* ─── PA-12 delegated authority ─── */

export const DELEGATION_STATUSES = ['PENDING', 'ACTIVE', 'REVOKED', 'EXPIRED'] as const;
export type DelegationStatus = (typeof DELEGATION_STATUSES)[number];

/**
 * The only roles a delegation may confer.
 *
 * Mirrored from DELEGABLE_PRODUCT_ADMIN_ROLE_CODES. The server refuses anything
 * else, and the reason for repeating the list here is that the form must not offer
 * a choice the server will reject: a select box that can produce a 400 teaches the
 * operator that the rules are arbitrary rather than that this one is a boundary.
 */
export const DELEGABLE_ROLE_CODES = [
  'EDUCORE_ADMIN',
  'LEARNHUB_ADMIN',
  'MARKET_ADMIN',
  'INSTITUTIONFLOW_ADMIN',
  'INTEGRATION_ADMIN',
  'SECURITY_ADMIN',
  'AUDIT_VIEWER',
] as const;

export type DelegableRoleCode = (typeof DELEGABLE_ROLE_CODES)[number];

export interface Delegation {
  id: string;
  delegatorPersonId: string;
  delegatorName?: string;
  delegatePersonId: string;
  delegateName?: string;
  sourceRoleAssignmentId: string;
  delegatedRoleDefinitionId?: string;
  delegatedRoleCode?: string;
  tenantId?: string;
  institutionId?: string;
  institutionName?: string;
  campusId?: string;
  campusName?: string;
  validFrom: string;
  validUntil: string;
  approvalLimit?: number;
  currency?: string;
  reason: string;
  status: DelegationStatus;
  approvedBy?: string;
  revokedAt?: string;
  revokedBy?: string;
  createdAt?: string;
}

export interface GrantDelegationRequest {
  delegatorPersonId: string;
  delegatePersonId: string;
  sourceRoleAssignmentId: string;
  delegatedRoleCode: DelegableRoleCode;
  /** Required. An unscoped delegation is platform-wide authority and is always refused. */
  institutionId: string;
  campusId?: string;
  validFrom: string;
  validUntil: string;
  approvalLimit?: number;
  currency?: string;
  reason: string;
}

/** What a delegation actually confers, resolved server-side rather than guessed. */
export interface InheritedPermissionReview {
  delegationId: string;
  delegatePersonId: string;
  delegatedRoleCode: string;
  institutionId?: string;
  campusId?: string;
  status: DelegationStatus;
  validFrom: string;
  validUntil: string;
  inheritedPermissions: string[];
  /** Denies survive everything. An ALLOW elsewhere does not overturn one. */
  deniedPermissions: string[];
  approvalLimit?: number;
  currency?: string;
  /** Non-empty means the escalation check was bypassed somewhere. Always shown. */
  exceedsDelegator: string[];
}

/* ─── PA-17 support access ─── */

export const SUPPORT_ACCESS_MODES = ['READ_ONLY', 'IMPERSONATION'] as const;
export type SupportAccessMode = (typeof SUPPORT_ACCESS_MODES)[number];

export const SUPPORT_ACCESS_STATUSES = [
  'PENDING_APPROVAL',
  'APPROVED',
  'ACTIVE',
  'DENIED',
  'REVOKED',
  'EXPIRED',
] as const;

export type SupportAccessStatus = (typeof SUPPORT_ACCESS_STATUSES)[number];

/** One shift. Mirrors MAX_SUPPORT_ACCESS_MINUTES on the server. */
export const MAX_SUPPORT_ACCESS_MINUTES = 480;
export const MIN_SUPPORT_ACCESS_MINUTES = 5;
export const MIN_SUPPORT_REASON_LENGTH = 20;

/**
 * Roles nobody may be impersonated out of, ever, with no override anywhere.
 *
 * Repeated here so the request form refuses before it submits and says why.
 * Impersonating someone who can approve money produces a payment record that says
 * they approved it, and no amount of logging on this side makes the finance
 * system's record less wrong. There is no flag on the API that turns this off, and
 * the UI must not imply there might be one — an "ask anyway" button teaches people
 * the rule is negotiable, which is the whole failure mode the rule exists to stop.
 */
export const NON_IMPERSONABLE_ROLE_CODES = [
  'BURSAR',
  'ACCOUNTANT',
  'CASHIER',
  'FINANCE_OFFICER',
  'FINANCIAL_CONTROLLER',
  'PAYROLL_OFFICER',
  'PROPRIETOR',
] as const;

export interface SupportAccessGrant {
  id: string;
  supportPersonId: string;
  supportPersonName?: string;
  supportKeycloakUserId?: string;
  tenantId?: string;
  institutionId: string;
  institutionName?: string;
  targetPersonId?: string;
  targetPersonName?: string;
  mode: SupportAccessMode;
  status: SupportAccessStatus;
  reason: string;
  ticketReference?: string;
  requestedDurationMinutes: number;
  approvedBy?: string;
  approvedAt?: string;
  deniedBy?: string;
  denialReason?: string;
  expiresAt?: string;
  startedAt?: string;
  endedAt?: string;
  revokedBy?: string;
  /** Always true on the wire. Kept as a field so a false value is visible, not silent. */
  impersonationBanner?: boolean;
  actionCount?: number;
  createdAt?: string;
}

export interface RequestSupportAccessRequest {
  institutionId: string;
  mode: SupportAccessMode;
  targetPersonId?: string;
  durationMinutes: number;
  reason: string;
  ticketReference?: string;
}

/** What `start` returns: the terms the session runs under. */
export interface SupportSessionDescriptor {
  grantId: string;
  mode: SupportAccessMode;
  institutionId: string;
  targetPersonId?: string;
  readOnly: boolean;
  impersonationBanner: boolean;
  expiresAt: string;
  startedAt: string;
}

/* ─── PA-05 feature flags ─── */

export const FEATURE_FLAG_SCOPES = ['INSTITUTION', 'CAMPUS', 'ROLE', 'ACADEMIC_YEAR'] as const;
export type FeatureFlagScope = (typeof FEATURE_FLAG_SCOPES)[number];

/**
 * Most specific first. Mirrors FLAG_SCOPE_PRECEDENCE in core.
 *
 * ROLE outranks CAMPUS deliberately: role-scoped flags are how a feature is piloted
 * with a few staff across a whole institution, and a campus-wide setting would
 * otherwise cancel the pilot for anyone on that campus. The screen renders this
 * array rather than restating the order in prose, so the two cannot disagree.
 */
export const FLAG_SCOPE_PRECEDENCE: readonly FeatureFlagScope[] = [
  'ACADEMIC_YEAR',
  'ROLE',
  'CAMPUS',
  'INSTITUTION',
];

export interface FeatureFlagSetting {
  id: string;
  institutionId: string;
  featureCode: string;
  scope: FeatureFlagScope;
  /** Empty string for INSTITUTION scope — never null, deliberately, on the server. */
  scopeValue: string;
  enabled: boolean;
  config?: Record<string, unknown> | null;
  version: number;
  lastChangeReason?: string | null;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FeatureFlagVersion {
  id: string;
  settingId?: string;
  featureCode: string;
  scope: FeatureFlagScope;
  scopeValue: string;
  enabled: boolean;
  config?: Record<string, unknown> | null;
  version: number;
  reason?: string | null;
  changedBy?: string;
  changedAt?: string;
  createdAt?: string;
}

export interface ResolvedFeatureFlag {
  featureCode: string;
  enabled: boolean;
  resolvedFromScope: FeatureFlagScope | null;
  resolvedFromScopeValue: string | null;
  config: Record<string, unknown> | null;
  version: number | null;
}

export interface SetFeatureFlagRequest {
  featureCode: string;
  scope: FeatureFlagScope;
  scopeValue?: string;
  enabled: boolean;
  config?: Record<string, unknown>;
  reason: string;
}

/**
 * A feature's declared dependencies, so the form can warn before it submits.
 *
 * The server refuses a change that breaks these with a 409. Showing the same rules
 * up front is not duplicated validation — it is the difference between "this is not
 * allowed, and here is what depends on it" and a rejection that arrives after the
 * operator has already decided.
 */
export interface FeatureDefinitionSummary {
  code: string;
  name: string;
  description: string | null;
  productCode: string | null;
  isActive: boolean;
  /**
   * False when this feature's metadata could not be parsed.
   *
   * Rendered, not ignored. `rulesReadable: false` with empty `requires` is a
   * different statement from a feature that genuinely has no dependencies, and
   * treating them the same tells an operator there is nothing to check when in
   * fact nothing could be checked.
   */
  rulesReadable: boolean;
  requires: string[];
  conflictsWith: string[];
  /** Present only when rulesReadable is false. Safe to show an operator. */
  rulesError?: string;
}

/* ─── PA-09 / PA-10 integrations and mappings ─── */

export const INTEGRATION_CONNECTOR_TYPES = [
  'EDUCORE',
  'IDENTITY',
  'DIGITAL_SWITCH',
  'PAYMENT_GATEWAY',
  'LMS',
  'SIS',
  'MESSAGING',
  'ACCOUNTING',
  'CUSTOM',
] as const;

export type IntegrationConnectorType = (typeof INTEGRATION_CONNECTOR_TYPES)[number];

export const MAPPED_ENTITY_TYPES = [
  'INSTITUTION',
  'CAMPUS',
  'ACADEMIC_YEAR',
  'PROGRAMME',
  'CLASS',
  'SUBJECT',
  'PERSON',
  'STUDENT',
  'STAFF',
  'GUARDIAN',
  'PAYMENT_ACCOUNT',
  'MERCHANT',
] as const;

export type MappedEntityType = (typeof MAPPED_ENTITY_TYPES)[number];

/**
 * `vault://<mount>/<path>#<key>` — a pointer to a credential, never a credential.
 *
 * The UI validates against this pattern before submitting for one reason: so that a
 * secret pasted into the field is refused in the browser and never leaves the
 * machine. A value that fails this test is not sent, not logged, and not echoed
 * back into the DOM. The server enforces the same rule; this is not a substitute
 * for that, it is a shorter blast radius when someone pastes the wrong thing.
 */
export const VAULT_REFERENCE_PATTERN =
  /^vault:\/\/[A-Za-z0-9][A-Za-z0-9._-]*(\/[A-Za-z0-9][A-Za-z0-9._-]*)+#[A-Za-z0-9._-]+$/;

export function isVaultReference(value: string): boolean {
  return VAULT_REFERENCE_PATTERN.test(value);
}

export interface IntegrationRegistration {
  id: string;
  institutionId: string;
  institutionName?: string;
  connectorType: IntegrationConnectorType;
  code: string;
  displayName: string;
  externalTenantId?: string | null;
  baseUrl?: string | null;
  state: IntegrationState;
  /** A pointer. Shown verbatim precisely because it is not a secret. */
  vaultSecretRef?: string | null;
  vaultSecretVersion?: string | null;
  connectionConfig?: Record<string, unknown> | null;
  lastSyncAt?: string | null;
  lastSyncStatus?: string | null;
  lastHealthCheckAt?: string | null;
  lastError?: string | null;
  registeredBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface RegisterIntegrationRequest {
  connectorType: IntegrationConnectorType;
  code: string;
  displayName: string;
  externalTenantId?: string;
  baseUrl?: string;
  vaultSecretRef?: string;
  vaultSecretVersion?: string;
  connectionConfig?: Record<string, unknown>;
}

export interface UpdateIntegrationRequest {
  displayName?: string;
  externalTenantId?: string;
  baseUrl?: string;
  vaultSecretRef?: string;
  vaultSecretVersion?: string;
  connectionConfig?: Record<string, unknown>;
}

export interface SetIntegrationStateRequest {
  state: IntegrationState;
  lastError?: string;
  responseTimeMs?: number;
}

export interface IntegrationMapping {
  id: string;
  registrationId: string;
  institutionId: string;
  entityType: MappedEntityType;
  /** A CampusOne id. Never a code, never a name — see IntegrationMapping in core. */
  internalId: string;
  externalId: string;
  /** WHICH external identifier this is. Two kinds for one entity is legitimate. */
  externalIdKind: string;
  isActive: boolean;
  supersededById?: string | null;
  activatedAt?: string;
  deactivatedAt?: string | null;
  createdBy?: string;
  metadata?: Record<string, unknown> | null;
}

export interface CreateMappingRequest {
  entityType: MappedEntityType;
  internalId: string;
  externalId: string;
  externalIdKind: string;
  metadata?: Record<string, unknown>;
  /** Set to replace an existing active mapping. Without it, a clash is a 409. */
  supersedeReason?: string;
}

export interface IntegrationMappingHistoryEntry {
  id: string;
  mappingId?: string;
  registrationId?: string;
  entityType?: MappedEntityType;
  internalId?: string;
  externalId?: string;
  action?: string;
  reason?: string | null;
  actor?: string;
  occurredAt?: string;
  createdAt?: string;
}

/* ─── Platform connections and institution mappings ─── */

/**
 * TWO RECORDS, BECAUSE THERE ARE TWO QUESTIONS.
 *
 * "CampusOne Core connects to EduCore in production, and is that connection
 * healthy?" is an environment-level question with one answer for the whole
 * platform. "Which EduCore school is Kabale Secondary?" is an institution-level
 * question with a different answer per tenant. They have different owners,
 * different lifetimes and — the part that matters on screen — different failure
 * modes: the Digital Switch can be perfectly healthy while one school's tenant
 * mapping has never been filled in, and that state is extremely common.
 *
 * The old single record made `institutionId` mandatory, which meant the shared
 * endpoint and shared credential were re-entered per institution, "is the Switch
 * up?" could not be asked without first choosing a school, and the healthy-but-
 * unmapped state had to be reported as either "the Switch is broken" or "the
 * school is fine". Both are lies. These two shapes are what let the console stop
 * telling them.
 *
 * Mirrored field-for-field from PlatformConnection and
 * InstitutionIntegrationMapping in campusone-core-service.
 */

export const INTEGRATION_SYSTEM_CODES = [
  'EDUCORE',
  'CAMPUSONE_IDENTITY',
  'KEYCLOAK',
  'LEARNHUB',
  'LMS',
  'SCORM_RUNTIME',
  'CAMPUSONE_MARKET',
  'INSTITUTIONFLOW',
  'INSTITUTION_SERVICES',
  'PAYMENT_ORCHESTRATION',
  'DIGITAL_SWITCH',
  'CREDISURE',
  'EMAIL',
  'SMS',
  'WHATSAPP',
  'OBJECT_STORAGE',
  'KAFKA',
  'REDIS',
  'AUDIT_SERVICE',
  'NOTIFICATION_SERVICE',
] as const;

export type IntegrationSystemCode = (typeof INTEGRATION_SYSTEM_CODES)[number];

export const INTEGRATION_ENVIRONMENTS = ['PRODUCTION', 'STAGING', 'DEVELOPMENT'] as const;

export type IntegrationEnvironment = (typeof INTEGRATION_ENVIRONMENTS)[number];

export const INTEGRATION_CAPABILITIES = [
  'SIS_STUDENT_RECORDS',
  'SIS_ENROLMENT',
  'SIS_ASSESSMENT',
  'SIS_TIMETABLE',
  'IDENTITY_PROVISIONING',
  'SINGLE_SIGN_ON',
  'ROLE_MANAGEMENT',
  'LMS_COURSE_DELIVERY',
  'LMS_GRADEBOOK',
  'SCORM_PLAYBACK',
  'MARKETPLACE_CATALOGUE',
  'MARKETPLACE_ORDERS',
  'WORKFLOW_ORCHESTRATION',
  'INSTITUTION_DIRECTORY',
  'PAYMENTS',
  'PAYOUTS',
  'CREDISURE_SYNC',
  'NOTIFICATION_SMS',
  'NOTIFICATION_WHATSAPP',
  'NOTIFICATION_EMAIL',
  'NOTIFICATION_PUSH',
  'OBJECT_STORAGE',
  'EVENT_STREAM',
  'CACHE',
  'AUDIT_SINK',
  'WEBHOOK_DELIVERY',
] as const;

export type IntegrationCapability = (typeof INTEGRATION_CAPABILITIES)[number];

export const CONNECTION_AUTHENTICATION_TYPES = [
  'NONE',
  'API_KEY',
  'BEARER_TOKEN',
  'BASIC',
  'HMAC_SIGNATURE',
  'OAUTH2_CLIENT_CREDENTIALS',
  'MUTUAL_TLS',
] as const;

export type ConnectionAuthenticationType = (typeof CONNECTION_AUTHENTICATION_TYPES)[number];

/**
 * Which system's traffic normally rides on which other system's wire.
 *
 * SMS, WhatsApp, email, payment orchestration and CrediSure reach CampusOne
 * THROUGH the CSKY Digital Switch — one host, one API key, one HMAC secret, one
 * tenant header. They are not five connections next to the Switch; they are five
 * things the Switch carries.
 *
 * The console needs this map for one specific reason: without it, an operator
 * looking for "the WhatsApp connection" finds nothing, concludes WhatsApp is not
 * configured, and goes off to register a second row describing the same wire. The
 * capability list on the Switch row is where WhatsApp actually lives, and the UI
 * has to say so out loud.
 */
export const BROKERED_BY: Readonly<Partial<Record<IntegrationSystemCode, IntegrationSystemCode>>> = {
  SMS: 'DIGITAL_SWITCH',
  WHATSAPP: 'DIGITAL_SWITCH',
  EMAIL: 'DIGITAL_SWITCH',
  PAYMENT_ORCHESTRATION: 'DIGITAL_SWITCH',
  CREDISURE: 'DIGITAL_SWITCH',
};

export function brokerFor(systemCode: IntegrationSystemCode): IntegrationSystemCode | undefined {
  return BROKERED_BY[systemCode];
}

export function isBrokeredSystem(systemCode: IntegrationSystemCode): boolean {
  return BROKERED_BY[systemCode] !== undefined;
}

/**
 * What each system can do when it is connected. Advisory — it seeds the capability
 * checkboxes on the registration form so the common case is one click, and an
 * unusual deployment can still be described by unticking something.
 */
export const DEFAULT_CAPABILITIES: Readonly<
  Record<IntegrationSystemCode, readonly IntegrationCapability[]>
> = {
  EDUCORE: ['SIS_STUDENT_RECORDS', 'SIS_ENROLMENT', 'SIS_ASSESSMENT', 'SIS_TIMETABLE'],
  CAMPUSONE_IDENTITY: ['IDENTITY_PROVISIONING', 'ROLE_MANAGEMENT'],
  KEYCLOAK: ['SINGLE_SIGN_ON', 'ROLE_MANAGEMENT'],
  LEARNHUB: ['LMS_COURSE_DELIVERY', 'LMS_GRADEBOOK'],
  LMS: ['LMS_COURSE_DELIVERY', 'LMS_GRADEBOOK'],
  SCORM_RUNTIME: ['SCORM_PLAYBACK'],
  CAMPUSONE_MARKET: ['MARKETPLACE_CATALOGUE', 'MARKETPLACE_ORDERS'],
  INSTITUTIONFLOW: ['WORKFLOW_ORCHESTRATION'],
  INSTITUTION_SERVICES: ['INSTITUTION_DIRECTORY'],
  PAYMENT_ORCHESTRATION: ['PAYMENTS', 'PAYOUTS'],
  DIGITAL_SWITCH: [
    'PAYMENTS',
    'PAYOUTS',
    'CREDISURE_SYNC',
    'NOTIFICATION_SMS',
    'NOTIFICATION_WHATSAPP',
    'NOTIFICATION_EMAIL',
    'WEBHOOK_DELIVERY',
  ],
  CREDISURE: ['CREDISURE_SYNC'],
  EMAIL: ['NOTIFICATION_EMAIL'],
  SMS: ['NOTIFICATION_SMS'],
  WHATSAPP: ['NOTIFICATION_WHATSAPP'],
  OBJECT_STORAGE: ['OBJECT_STORAGE'],
  KAFKA: ['EVENT_STREAM'],
  REDIS: ['CACHE'],
  AUDIT_SERVICE: ['AUDIT_SINK'],
  NOTIFICATION_SERVICE: ['NOTIFICATION_PUSH'],
};

/**
 * For a capability carried by `carrier`, the system it would have belonged to if
 * it were integrated directly — but only when that system is one the carrier
 * brokers.
 *
 * This is what turns a bare "NOTIFICATION_WHATSAPP" chip on the Digital Switch row
 * into the sentence an operator needs: WhatsApp arrives through the Switch, and
 * there is no separate WhatsApp connection to go looking for.
 */
export function brokeredOrigin(
  carrier: IntegrationSystemCode,
  capability: IntegrationCapability,
): IntegrationSystemCode | undefined {
  for (const code of INTEGRATION_SYSTEM_CODES) {
    if (BROKERED_BY[code] !== carrier) continue;
    if (DEFAULT_CAPABILITIES[code].includes(capability)) return code;
  }
  return undefined;
}

export interface ConnectionRetryPolicy {
  maxAttempts: number;
  backoffMs: number;
  retryOn: number[];
}

/**
 * One shared service connection, per environment.
 *
 * `credentialVaultReference` is a POINTER and is displayed verbatim for that
 * reason — being able to read which vault path is in use is how a rotation is
 * verified without anybody fetching the value. There is no field anywhere in this
 * console that accepts the value itself.
 *
 * `status` and `lastSuccessfulAt` travel together on purpose. The server refuses
 * to record HEALTHY without a successful probe behind it, so a connection with no
 * `lastSuccessfulAt` has either never been contacted or has never once answered —
 * and neither of those is green.
 */
export interface PlatformConnection {
  id: string;
  systemCode: IntegrationSystemCode;
  displayName: string;
  environment: IntegrationEnvironment;
  baseUrl?: string | null;
  /** Probed instead of baseUrl when set — a service root often 404s while healthy. */
  healthCheckUrl?: string | null;
  apiVersion?: string | null;
  authenticationType: ConnectionAuthenticationType;
  /** The public half of the credential. Safe to show. */
  clientId?: string | null;
  /** `vault://<mount>/<path>#<key>`. A pointer, never a credential. */
  credentialVaultReference?: string | null;
  timeoutMs?: number;
  retryPolicy?: ConnectionRetryPolicy | null;
  status: IntegrationState;
  /** When a probe last ran, whatever it concluded. Null means never probed. */
  lastCheckedAt?: string | null;
  /** When a probe last SUCCEEDED. The only evidence this has ever worked. */
  lastSuccessfulAt?: string | null;
  capabilities: IntegrationCapability[];
  lastError?: string | null;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface RegisterPlatformConnectionRequest {
  systemCode: IntegrationSystemCode;
  displayName: string;
  environment: IntegrationEnvironment;
  baseUrl?: string;
  healthCheckUrl?: string;
  apiVersion?: string;
  authenticationType: ConnectionAuthenticationType;
  clientId?: string;
  credentialVaultReference?: string;
  timeoutMs?: number;
  capabilities: IntegrationCapability[];
}

/**
 * Amends the endpoint and its operational settings.
 *
 * `systemCode` and `environment` are absent by design: they are the identity of
 * the row and the pair the uniqueness constraint is built on. Changing either is
 * registering a different connection, and doing it in place would silently
 * re-point every institution mapping that hangs off this id.
 *
 * `credentialVaultReference` is absent too — rotating it is its own action with
 * its own reason, because it invalidates the last health verdict.
 */
export interface UpdatePlatformConnectionRequest {
  displayName?: string;
  baseUrl?: string;
  healthCheckUrl?: string;
  apiVersion?: string;
  authenticationType?: ConnectionAuthenticationType;
  clientId?: string;
  timeoutMs?: number;
  capabilities?: IntegrationCapability[];
}

/**
 * Points the connection at a different vault path.
 *
 * Takes a reference and a reason, and nothing else. There is deliberately no
 * variant of this request that carries a secret value: the server rejects one, and
 * a field that accepts it invites somebody to paste a live key into a form whose
 * state ends up in crash reports.
 */
export interface RotateCredentialReferenceRequest {
  credentialVaultReference: string;
  /** The public half, when it rotates alongside the secret. Never the secret. */
  clientId?: string;
  reason: string;
}

/**
 * One connection's health as the SERVER judges it.
 *
 * `reporting` is not derivable from `status`, which is why it is carried rather
 * than computed here: a connection with no address at all is UNKNOWN, one nothing
 * has ever probed is NOT_REPORTING, and neither is a fault. `wouldRequire` says
 * what would have to happen for the connection to be able to answer at all —
 * without it, "nothing reporting" is a dead end rather than a task.
 */
export interface ConnectionHealthView {
  id: string;
  systemCode: IntegrationSystemCode;
  displayName: string;
  environment: IntegrationEnvironment;
  status: IntegrationState;
  reporting: OperationalState;
  capabilities: IntegrationCapability[];
  lastCheckedAt?: string | null;
  lastSuccessfulAt?: string | null;
  lastError?: string | null;
  wouldRequire?: string;
}

/**
 * What a server-side probe found. The connection and the verdict come back
 * together because they are different facts — `statusRecorded` false means the
 * probe ran but the status column was left alone.
 */
export interface PlatformConnectionProbeResult {
  connection: PlatformConnection;
  outcome: IntegrationProbeResult;
  statusRecorded: boolean;
}

/**
 * One institution's identity inside one connected system.
 *
 * THREE EXTERNAL IDENTIFIERS, NOT ONE, and they are not synonyms.
 * `externalTenantId` is the partition key the far end routes on — the Switch's
 * `X-Tenant-Id`, an LMS tenant. `externalInstitutionId` is the school record
 * inside that partition — an EduCore school id. `externalOrganizationId` is the
 * counterparty the money settles against — a CrediSure organisation. A school that
 * shares a Switch tenant with its sister campus but has its own CrediSure
 * organisation is ordinary, not exotic.
 *
 * `status` is the MAPPING's status and says nothing about the connection's health.
 * `verifiedAt` null is the normal starting state: an identifier somebody typed
 * into a form is a claim, not a fact.
 */
export interface InstitutionIntegrationMapping {
  id: string;
  institutionId: string;
  platformConnectionId: string;
  externalTenantId?: string | null;
  externalInstitutionId?: string | null;
  externalOrganizationId?: string | null;
  status: IntegrationState;
  capabilities: IntegrationCapability[];
  configuration?: Record<string, unknown> | null;
  verifiedAt?: string | null;
  verifiedBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** Present when the server joins the connection in. Never assumed. */
  connection?: PlatformConnection;
}

export interface CreateInstitutionMappingRequest {
  platformConnectionId: string;
  externalTenantId?: string;
  externalInstitutionId?: string;
  externalOrganizationId?: string;
  capabilities?: IntegrationCapability[];
  /** Non-secret per-institution settings only. Screened server-side at any depth. */
  configuration?: Record<string, unknown>;
}

export interface UpdateInstitutionMappingRequest {
  externalTenantId?: string;
  externalInstitutionId?: string;
  externalOrganizationId?: string;
  capabilities?: IntegrationCapability[];
  configuration?: Record<string, unknown>;
}

/* ─── PA-03 institution structure ─── */

export const INSTITUTION_GROUP_TYPES = [
  'SCHOOL_GROUP',
  'EDUCATION_AUTHORITY',
  'UNIVERSITY_SYSTEM',
  'ADMINISTRATIVE',
] as const;

export type InstitutionGroupType = (typeof INSTITUTION_GROUP_TYPES)[number];

export const GROUP_MEMBERSHIP_ROLES = ['HEAD_OFFICE', 'MEMBER', 'SUBSIDIARY', 'AFFILIATE'] as const;
export type GroupMembershipRole = (typeof GROUP_MEMBERSHIP_ROLES)[number];

/** Structure codes reach URLs, exports and external mappings, so they are constrained. */
export const STRUCTURE_CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]{1,48}[A-Z0-9]$/;

export interface Campus {
  id: string;
  institutionId: string;
  code: string;
  name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  countryCode?: string | null;
  status?: string;
}

export interface Branch {
  id: string;
  institutionId: string;
  campusId: string;
  code: string;
  name: string;
  /** At most one per institution, enforced by a partial unique index in the database. */
  isHeadOffice: boolean;
  address?: string | null;
  city?: string | null;
  countryCode?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  status?: string;
}

export interface InstitutionGroup {
  id: string;
  code: string;
  name: string;
  type: InstitutionGroupType;
  description?: string | null;
  countryCode?: string | null;
  isActive?: boolean;
}

export interface InstitutionGroupMember {
  id: string;
  groupId: string;
  institutionId: string;
  institutionName?: string;
  role: GroupMembershipRole;
  joinedAt?: string;
  leftAt?: string | null;
}

export interface GroupMembershipSummary {
  group: InstitutionGroup;
  role: GroupMembershipRole;
}

export interface InstitutionStructure {
  institution?: PlatformInstitutionRecord;
  campuses: Campus[];
  branches: Branch[];
  groups: GroupMembershipSummary[];
}

/**
 * What POST /platform/integrations/:id/health-check returns.
 *
 * Three fields, not one registration. The probe OUTCOME and the recorded STATE are
 * not the same thing — a timeout and an unreachable host are both non-healthy to
 * the platform, but one means look at the connector and the other means look at
 * the address — and `stateRecorded` says whether the verdict was actually stored.
 * The console unwrapped this as `{ integration }`, an envelope key that does not
 * exist, so a working probe threw a parse error and the button reported the test
 * as unavailable.
 */
/**
 * What the probe found, as a closed set rather than a boolean.
 *
 * TIMED_OUT and UNREACHABLE are distinct on purpose: unreachable means nothing is
 * listening and the next action is to check the address; timed out means something
 * is listening and did not answer, and the next action is to look at the
 * connector. NOT_PROBEABLE is not a failure — it means no base URL is recorded,
 * and drawing it red would put a fault against an integration whose only problem
 * is that nobody has finished configuring it.
 */
export const PROBE_OUTCOME_KINDS = [
  'REACHABLE',
  'SLOW',
  'TIMED_OUT',
  'UNREACHABLE',
  'REJECTED',
  'NOT_PROBEABLE',
] as const;

export type ProbeOutcomeKind = (typeof PROBE_OUTCOME_KINDS)[number];

export interface IntegrationProbeResult {
  kind: ProbeOutcomeKind;
  responseTimeMs: number | null;
  httpStatus: number | null;
  /** Safe to show an operator. Never contains the resolved credential. */
  detail: string;
}

export interface IntegrationHealthCheckResult {
  registration: IntegrationRegistration;
  outcome: IntegrationProbeResult;
  stateRecorded: boolean;
}

/**
 * What POST /platform/structure/institutions takes.
 *
 * `tenantId` omitted creates a tenant named after the institution code, which is
 * right for a standalone institution and wrong for one joining a group that already
 * has a tenant — Core cannot tell the two apart, so the choice is the operator's.
 * The campus fields default to MAIN / "Main Campus" server-side.
 */
export interface BootstrapInstitutionRequest {
  onboardingCaseId: string;
  tenantId?: string;
  primaryCampusCode?: string;
  primaryCampusName?: string;
}

export interface CreateCampusRequest {
  code: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  countryCode?: string;
}

export interface CreateBranchRequest {
  campusId: string;
  code: string;
  name: string;
  isHeadOffice?: boolean;
  address?: string;
  city?: string;
  countryCode?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface CreateGroupRequest {
  code: string;
  name: string;
  type: InstitutionGroupType;
  description?: string;
  countryCode?: string;
  /**
   * The tenant the group's institutions share. Omitted, the group gets its own
   * tenant named after the group code — right for a standalone complexe scolaire
   * and wrong for one joining an existing tenant, and Core cannot tell the two
   * apart, so the choice is the operator's. Mirrors BootstrapInstitutionRequest.
   */
  tenantId?: string;
}

/**
 * What PATCH /platform/structure/groups/:groupId accepts.
 *
 * A partial update: every field is optional and only the ones present are
 * changed. There is no deactivation here — `isActive` exists on the row but no
 * route toggles it yet, so the settings tab does not offer a control that would
 * 400 (or worse, silently do nothing).
 */
export interface UpdateGroupRequest {
  name?: string;
  description?: string;
  countryCode?: string;
}

/**
 * One member institution's row in the group analytics projection.
 *
 * `type` and `status` are the INSTITUTION's values, not the group's — a group
 * analytics screen shows schools, and "School group / Active" would be the type
 * and status of the row this belongs to, which is a different entity.
 */
export interface GroupAnalyticsInstitution {
  institutionId: string;
  code: string;
  name: string;
  type?: string | null;
  status?: string | null;
  currency?: string | null;
  /** Product codes this member is subscribed to, not the product definitions. */
  productSubscriptions?: string[];
  campuses?: number;
  branches?: number;
  /** The active academic year label EduCore reported, when this member answered. */
  academicYear?: string | null;
}

export interface GroupCurrencyBreakdownEntry {
  currency: string;
  institutionCount: number;
}

export interface GroupProductSubscriptionEntry {
  productCode: string;
  institutionCount: number;
}

/**
 * One institution's fee collection, as EduCore aggregates it and core relays it.
 *
 * AMOUNTS ARE NATIVE UNITS — the same minor-unit scale the institution's own
 * currency setting records (XAF amounts are whole francs). The console formats
 * them for display with the currency code that accompanies them, exactly as the
 * institution console's financial panels do; nothing here divides or converts.
 *
 * `collectionRate` is 0–100 and null is a real answer: a member with nothing
 * billed has no rate, and a screen that drew 0% would report total failure to
 * collect. Absent means "not recorded", never zero.
 */
export interface GroupFinancialTotals {
  students: number;
  staff: number;
  /** Guardians (Parent records) across the group; absent when not every school reported. */
  parents?: number;
  /** Teaching groups of the active academic year; absent when not every school reported. */
  classGroups?: number;
  /** Settled receipts against the year's bills; absent when not every school reported. */
  payments?: number;
  expectedFees: number;
  collectedFees: number;
  outstandingFees: number;
  collectionRate: number | null;
  /**
   * The group's admissions funnel for the active academic year.
   *
   * PRESENT ONLY WHEN EVERY AVAILABLE INSTITUTION REPORTED EVERY COUNT — a
   * partial total would under-count, so an absent field here means "not
   * aggregated", never zero. `conversionRate` is 0–100 when applications were
   * received and null when there were none to convert.
   */
  admissions?: GroupAdmissions;
  /**
   * The group's class-group capacity for the active academic year. Same
   * all-or-nothing rule as `admissions`. `occupancyRate` is 0–100, or null
   * when no capacity is configured anywhere in the group.
   */
  capacity?: GroupCapacity;
  /**
   * The group's cohort progression. Same all-or-nothing rule as `admissions`.
   * `internalProgressionRate` is 0–100, or null when the eligible cohort was
   * empty.
   */
  progression?: GroupProgression;
}

/**
 * One member's admissions funnel, as EduCore aggregates it and core relays it.
 *
 * Every field is `undefined` when core did not send it and `null` when EduCore
 * reported "no value" (the conversion rate is null when there were no
 * applications). Both render as an em dash — never as 0 or 0%. The same shape
 * doubles as the group totals, where a field is only present when every
 * available institution reported that exact count.
 */
export interface GroupAdmissions {
  applications?: number | null;
  offered?: number | null;
  accepted?: number | null;
  enrolled?: number | null;
  conversionRate?: number | null;
}

/**
 * One member's class-group capacity, as EduCore aggregates it and core relays
 * it. `occupancyRate` is 0–100, or null when no capacity is configured — a
 * school that configured nothing has no occupancy, and a screen that drew 0%
 * would report full failure to fill empty rooms. Absent means "not recorded",
 * never zero. The same shape doubles as the group totals.
 */
export interface GroupCapacity {
  configuredClassGroups?: number | null;
  totalCapacity?: number | null;
  totalEnrolled?: number | null;
  occupancyRate?: number | null;
}

/**
 * One member's cohort progression between academic years, as EduCore
 * aggregates it and core relays it. `internalProgressionRate` is 0–100, or
 * null when the eligible cohort was empty. Absent means "not recorded", never
 * zero. The same shape doubles as the group totals.
 */
export interface GroupProgression {
  eligibleCohort?: number | null;
  continuedWithinGroup?: number | null;
  didNotContinueWithinGroup?: number | null;
  unknownDestination?: number | null;
  internalProgressionRate?: number | null;
}

/**
 * One member's row of the financial aggregation.
 *
 * Every field after `available` is optional: a member whose tenant has not
 * reported to EduCore is `available: false` and carries none of the figures,
 * and a partially settled member may carry some and not others. Rendering an
 * absent figure as zero is exactly the lie this phase exists to avoid, so the
 * UI shows an em dash for anything the server did not send.
 */
export interface GroupFinancialInstitution {
  institutionId: string;
  available: boolean;
  students?: number;
  staff?: number;
  /** Guardians (Parent records) of this school; absent when it did not report them. */
  parents?: number;
  /** Teaching groups of the active academic year; absent when not reported. */
  classGroups?: number;
  /** Settled receipts against the year's bills; absent when not reported. */
  payments?: number;
  expectedFees?: number;
  collectedFees?: number;
  outstandingFees?: number;
  collectionRate?: number | null;
  currency?: string | null;
  /**
   * This school's admissions funnel. null means EduCore sent the slot but no
   * value for it ("not reported"), which renders the same em dash as absence.
   */
  admissions?: GroupAdmissions | null;
  /** This school's class-group capacity; null means sent but not reported. */
  capacity?: GroupCapacity | null;
  /**
   * This school's attendance rate, 0–100. Deliberately NOT aggregated at the
   * group level — the contract has no group attendance figure and the UI must
   * never invent one.
   */
  attendanceRate?: number | null;
  /** This school's cohort progression; null means sent but not reported. */
  progression?: GroupProgression | null;
  /**
   * When EduCore last refreshed this school's academic figures. Per institution
   * only, never aggregated. null means sent but not reported.
   */
  lastUpdatedAt?: string | null;
}

/**
 * The optional `financial` section of the group analytics projection.
 *
 * ADDITIVE AND ABSENT BY DESIGN. Core gains this field in the analytics phase;
 * a deployment that has not caught up returns the projection without it, and
 * the finance tab must then say "not available yet" rather than showing zeros.
 * `totals` may also be null while `institutions` carries rows — the aggregate
 * is computed separately from the per-member figures.
 */
export interface GroupFinancial {
  available: boolean;
  totals: GroupFinancialTotals | null;
  institutions: GroupFinancialInstitution[];
}

/**
 * GET /platform/structure/groups/:groupId/analytics.
 *
 * The whole answer to "how is this group doing" in one document, generated by
 * core at request time. The finance figures arrive in two layers: the readiness
 * signals (currency per institution) always, and the aggregated fee collection
 * (`financial`) only when the analytics phase has produced it.
 */
export interface GroupAnalytics {
  groupId: string;
  groupCode: string;
  groupName: string;
  groupType: InstitutionGroupType;
  institutionCount: number;
  activeInstitutionCount: number;
  campusCount: number;
  branchCount: number;
  currencyBreakdown: GroupCurrencyBreakdownEntry[];
  productSubscriptions: GroupProductSubscriptionEntry[];
  institutions: GroupAnalyticsInstitution[];
  financial?: GroupFinancial;
  generatedAt?: string;
}

/* ─── Bulk product administration (analytics phase) ─── */

/**
 * The two group-level product verbs core accepts on the bulk endpoint.
 *
 * SUBSCRIBE creates the activation in PENDING_CONFIGURATION, exactly as the
 * single-institution subscribe does; UNSUBSCRIBE ends the subscription. The
 * action is a verb about the whole group, never a state machine step, which is
 * why the endpoint takes these and not a ProductState.
 */
export const BULK_PRODUCT_ACTIONS = ['SUBSCRIBE', 'UNSUBSCRIBE'] as const;
export type BulkProductAction = (typeof BULK_PRODUCT_ACTIONS)[number];

/** One member the action took effect on. `state` is the resulting activation state. */
export interface BulkProductAppliedEntry {
  institutionId: string;
  state?: string;
}

/**
 * One member the action could not be applied to, and why.
 *
 * The reason codes are core's vocabulary: NOT_A_MEMBER (the institution is not
 * in the group), NOT_FOUND (unknown institution id), ALREADY_APPLIED (subscribing
 * to something already held, or unsubscribing from something never subscribed).
 * Typed as a union with a string escape hatch so a new reason added server-side
 * still renders as its own label rather than blanking the row.
 */
export type BulkProductSkipReason = 'NOT_A_MEMBER' | 'NOT_FOUND' | 'ALREADY_APPLIED' | string;

export interface BulkProductSkippedEntry {
  institutionId: string;
  reason: BulkProductSkipReason;
}

/**
 * What POST /platform/structure/groups/:groupId/products returns.
 *
 * The result is not a single row: the server applies the action per member and
 * reports every outcome separately, because a group can contain institutions the
 * action must not silently pass over. `missingInstitutions` names ids that were
 * in the request but belong to no member at all — the caller's copy of the
 * membership list was stale, and that is worth saying out loud.
 */
export interface BulkProductApplyResult {
  productCode: string;
  action: BulkProductAction;
  applied: BulkProductAppliedEntry[];
  skipped: BulkProductSkippedEntry[];
  missingInstitutions: string[];
}

/**
 * POST /platform/structure/groups/:groupId/products.
 *
 * `institutionIds` omitted means every current member — the common case for a
 * group-level subscribe. Present, the action is scoped to exactly those members
 * and every other member is left alone.
 */
export interface BulkApplyGroupProductRequest {
  productCode: string;
  action: BulkProductAction;
  institutionIds?: string[];
}

/* ─── PA-06 branding ─── */

export const BRANDING_IMAGE_FORMATS = ['PNG', 'JPEG', 'WEBP', 'ICO'] as const;
export type BrandingImageFormat = (typeof BRANDING_IMAGE_FORMATS)[number];

export const DOMAIN_VERIFICATION_STATES = [
  'NOT_CONFIGURED',
  'PENDING_DNS',
  'VERIFIED',
  'FAILED',
] as const;

export type DomainVerificationState = (typeof DOMAIN_VERIFICATION_STATES)[number];

/**
 * The stored branding row, with its nulls intact.
 *
 * Read from `GET /branding/:id/settings`, not from `GET /branding/:id`. The bare
 * route returns RESOLVED branding with platform defaults already applied, which
 * is the right answer for a portal painting a page and the wrong one for an
 * editor: it makes every default look like a value this institution chose, and
 * saving the form would then write the defaults in as overrides.
 *
 * Images are object keys and measurements, not URLs. The console had `logoUrl`
 * and `faviconUrl`, which core has never had — the uploader measures the file and
 * this API decides whether the measurement is acceptable.
 */
export interface InstitutionBranding {
  id?: string;
  institutionId: string;
  logoObjectKey?: string | null;
  logoFormat?: BrandingImageFormat | null;
  logoWidthPx?: number | null;
  logoHeightPx?: number | null;
  logoByteSize?: number | null;
  faviconObjectKey?: string | null;
  faviconFormat?: BrandingImageFormat | null;
  loginBackgroundKey?: string | null;
  emailHeaderKey?: string | null;
  /**
   * Object storage key of the imported package's stylesheet. The bytes are
   * streamed through the same authenticated asset route as the logo.
   */
  customCssKey?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  loginBackgroundColor?: string | null;
  portalTitle?: string | null;
  defaultLanguage?: string | null;
  supportEmail?: string | null;
  supportPhone?: string | null;
  supportUrl?: string | null;
  emailSenderName?: string | null;
  emailSenderAddress?: string | null;
  /** Sending from an unverified address is refused downstream, so it is shown. */
  emailSenderVerified?: boolean;
  loginHeadline?: string | null;
  loginSubtitle?: string | null;
  welcomeText?: string | null;
  subdomain?: string | null;
  customDomain?: string | null;
  customDomainState?: DomainVerificationState;
  customDomainVerifiedAt?: string | null;
  customDomainCheckedAt?: string | null;
  customDomainFailureReason?: string | null;
  /**
   * Enforced by a database CHECK, not by this field.
   *
   * There is no way to switch it off through the API and there must not be one.
   * It is read-only here so the panel can show that it is on, never set it.
   */
  poweredByAttribution?: boolean;
  customCss?: Record<string, unknown> | null;
  lastChangedBy?: string | null;
  updatedAt?: string;
}

export interface BrandingAsset {
  objectKey: string;
  format: BrandingImageFormat;
  widthPx: number;
  heightPx: number;
  byteSize: number;
}

/**
 * The result of storing a branding image, returned by core.
 *
 * The objectKey is generated by the SERVICE inside the institution's own
 * namespace — a branding update will refuse any key that was not issued this
 * way, which is what stops one institution writing over another's asset.
 *
 * `uploadUrl` and `expiresInSeconds` belong to the older presigned flow and are
 * absent when core stored the bytes itself. They are kept OPTIONAL rather than
 * deleted because that endpoint still exists for large objects; nothing in this
 * app reads them.
 */
export interface BrandingAssetUploadTicket {
  objectKey: string;
  contentType: string;
  /** Present only on a presigned ticket. */
  uploadUrl?: string;
  /** Present only on a presigned ticket. */
  expiresInSeconds?: number;
  /** Present only when core stored the bytes and therefore knows the size. */
  byteSize?: number;
}

/**
 * A branding patch. `PUT /branding/:institutionId`.
 *
 * Every field is optional because branding is edited one control at a time, and
 * core merges the patch onto the stored row before grading the RESULT — so a
 * colour is checked against the background it will actually sit on.
 *
 * There is no `reason` field and there must not be one: the DTO validates with
 * `forbidNonWhitelisted`, so the reason this console used to attach made the
 * entire request a 400 and no branding change ever saved. There is no
 * `poweredByAttribution` either; the platform attribution is enforced in three
 * places and is not negotiable through an API.
 */
export interface UpdateBrandingRequest {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  loginBackgroundColor?: string;
  portalTitle?: string;
  defaultLanguage?: string;
  supportEmail?: string;
  supportPhone?: string;
  supportUrl?: string;
  emailSenderName?: string;
  emailSenderAddress?: string;
  loginHeadline?: string;
  loginSubtitle?: string;
  subdomain?: string;
  logo?: BrandingAsset;
  favicon?: BrandingAsset;
  loginBackground?: BrandingAsset;
  emailHeader?: BrandingAsset;
  customCss?: Record<string, unknown>;
}

/**
 * A branding package: the design files an institution administrator picks in a
 * folder and imports in one action.
 *
 * The browser reads the files as text and POSTs them in a single JSON body —
 * the identity BFF re-serializes bodies as JSON, so multipart would not
 * survive the hop. `tokens` is the W3C design-tokens JSON (or its flat
 * rendition); `css` is the token stylesheet; `markdown` is the design
 * description.
 */
export interface ImportBrandingPackageRequest {
  packageName?: string;
  description?: string;
  tokens: Record<string, unknown>;
  css?: string;
  markdown?: string;
}

/**
 * The DNS record that proves a domain, and the state of the claim.
 *
 * A custom domain is its own endpoint, not a field on the branding patch. It is
 * stored PENDING_DNS and is NOT served until a check has passed; there is no
 * administrative override, because downstream an override is indistinguishable
 * from a verified domain. Setting it through the branding form — which is what
 * the console tried to do — could never have worked.
 */
export interface DomainVerificationInstruction {
  domain: string;
  recordType: 'TXT';
  recordName: string;
  recordValue: string;
  state: DomainVerificationState;
}

export interface RestoreBrandingDefaultsRequest {
  reason: string;
  /** Off by default: a restore is usually about colours, and re-uploading a logo
   * needs the original file, which the administrator may not still have. */
  clearAssets?: boolean;
}

export const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/* ─── PA-11 policies ─── */

/**
 * Policies are a KEYED SET, not a table of rows with kinds and scopes.
 *
 * The console was written against an invented model — `PolicySetting` with an
 * `id`, a `kind`, a `scope` and a PATCH per row — and none of it exists. Core
 * publishes a whole platform DOCUMENT as a version, and an institution deviates
 * from it one key at a time with an override that has a reason and an end date.
 * The two models are not reconcilable by renaming fields, which is why this
 * section is a replacement rather than an edit: the old one described a product
 * that was never built.
 */
export const POLICY_KEYS = [
  'DEFAULT_LANGUAGE',
  'DEFAULT_TIMEZONE',
  'SUPPORTED_CURRENCIES',
  'PASSWORD_MIN_LENGTH',
  'PASSWORD_REQUIRE_MIXED_CASE',
  'PASSWORD_REQUIRE_NUMBER',
  'PASSWORD_REQUIRE_SYMBOL',
  'PASSWORD_HISTORY_COUNT',
  'PASSWORD_MAX_AGE_DAYS',
  'MFA_REQUIRED_FOR_STAFF',
  'MFA_REQUIRED_FOR_ADMINS',
  'SESSION_IDLE_TIMEOUT_MINUTES',
  'SESSION_ABSOLUTE_TIMEOUT_HOURS',
  'GUARDIAN_CONSENT_REQUIRED',
  'MINOR_AGE_THRESHOLD_YEARS',
  'MINOR_DIRECT_MESSAGING_ALLOWED',
  'MINOR_MARKETPLACE_PURCHASE_ALLOWED',
  'DOCUMENT_RETENTION_DAYS',
  'AUDIT_RETENTION_DAYS',
  'NOTIFICATION_CHANNELS_ENABLED',
  'NOTIFICATION_QUIET_HOURS_ENFORCED',
  'ALLOWED_PAYMENT_CHANNELS',
  'PRODUCT_APPROVAL_POLICY',
  'MERCHANT_APPROVAL_POLICY',
  'CREATOR_APPROVAL_POLICY',
  'DATA_EXPORT_POLICY',
  'SUPPORT_ACCESS_POLICY',
] as const;

export type PolicyKey = (typeof POLICY_KEYS)[number];

/** A policy value is one of exactly four shapes. Core refuses anything else. */
export type PolicyValue = string | number | boolean | string[];

export const POLICY_SOURCES = ['INSTITUTION_OVERRIDE', 'PLATFORM_VERSION', 'BASELINE'] as const;
export type PolicySource = (typeof POLICY_SOURCES)[number];

/**
 * Why an override stopped applying.
 *
 * Rendered rather than hidden. An override that has fallen below a floor CSKY
 * raised since it was granted is reported as suppressed, and an institution that
 * cannot see that will keep believing it has a 30-minute session timeout.
 */
export interface PolicyViolation {
  key: PolicyKey;
  attempted: PolicyValue;
  /** The floor, already rendered as words by the server. */
  minimum: string;
  message: string;
}

export interface ResolvedPolicy {
  key: PolicyKey;
  value: PolicyValue;
  source: PolicySource;
  suppressedOverride?: PolicyViolation;
}

export interface ResolvedPolicySet {
  institutionId: string;
  asOf: string;
  /** Null when nothing is published and the in-code baseline answered. */
  platformVersionNumber: number | null;
  policies: ResolvedPolicy[];
}

export const POLICY_VERSION_STATUSES = ['DRAFT', 'PUBLISHED', 'SUPERSEDED'] as const;
export type PolicyVersionStatus = (typeof POLICY_VERSION_STATUSES)[number];

export interface PlatformPolicyVersion {
  id: string;
  versionNumber: number;
  status: PolicyVersionStatus;
  document: Partial<Record<PolicyKey, PolicyValue>>;
  effectiveFrom: string;
  effectiveTo?: string | null;
  changeReason: string;
  createdBy: string;
  publishedBy?: string | null;
  publishedAt?: string | null;
  createdAt?: string;
}

export interface InstitutionPolicyOverride {
  id: string;
  institutionId: string;
  policyKey: PolicyKey;
  value: PolicyValue;
  reason: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  revokedAt?: string | null;
  revokedBy?: string | null;
  grantedBy: string;
  createdAt?: string;
}

export const POLICY_CONSTRAINT_KINDS = [
  'NUMBER_AT_LEAST',
  'NUMBER_AT_MOST',
  'BOOLEAN_MUST_REMAIN_TRUE',
  'ENUM_AT_LEAST_AS_STRONG',
  'SET_SUBSET_OF',
  'NO_SECURITY_FLOOR',
] as const;

export type PolicyConstraintKind = (typeof POLICY_CONSTRAINT_KINDS)[number];

/**
 * One entry of the CSKY security floor, with the reason for the bound.
 *
 * `why` is shown to the operator verbatim. The floor exists so an override cannot
 * make a policy weaker, and an operator who is refused without being told why
 * learns that the platform is capricious rather than that this one is a boundary.
 */
export interface PolicyConstraint {
  kind: PolicyConstraintKind;
  why: string;
  bound?: number;
  floor?: string;
  strength?: Record<string, number>;
  permitted?: string[];
}

export type PolicyMinimums = Partial<Record<PolicyKey, PolicyConstraint>>;

export interface GrantPolicyOverrideRequest {
  policyKey: PolicyKey;
  value: PolicyValue;
  reason: string;
  effectiveFrom?: string;
  effectiveTo?: string;
}

/* ─── PA-15 master data ─── */

/** Mirrored from MasterDataDomain. There are sixteen, and none of them is a "set". */
export const MASTER_DATA_DOMAINS = [
  'INSTITUTION_TYPE',
  'PRODUCT_DEFINITION',
  'FEATURE_DEFINITION',
  'ROLE_TEMPLATE',
  'PERMISSION_TEMPLATE',
  'MERCHANT_CATEGORY',
  'CREATOR_CATEGORY',
  'SERVICE_CATEGORY',
  'LANGUAGE',
  'COUNTRY',
  'CURRENCY',
  'INTEGRATION_TYPE',
  'NOTIFICATION_TEMPLATE',
  'WORKFLOW_TEMPLATE',
  'DOCUMENT_TYPE',
  'SUPPORT_CATEGORY',
] as const;

export type MasterDataDomain = (typeof MASTER_DATA_DOMAINS)[number];

export const MASTER_DATA_VALUE_STATUSES = [
  'DRAFT',
  'ACTIVE',
  'DEPRECATED',
  'SUPERSEDED',
  'RETIRED',
] as const;

export type MasterDataValueStatus = (typeof MASTER_DATA_VALUE_STATUSES)[number];

export interface MasterDataReference {
  domain: MasterDataDomain;
  code: string;
}

/**
 * One version of one reference value.
 *
 * There is no `active` boolean and no `labelFr`. Core versions values rather than
 * editing them, so the state is a five-member status, and the French label is not
 * a column — the console's form asked for one and core's DTO would have rejected
 * the field outright.
 */
export interface MasterDataValue {
  id: string;
  domain: MasterDataDomain;
  code: string;
  version: number;
  status: MasterDataValueStatus;
  label: string;
  description?: string | null;
  payload?: Record<string, unknown>;
  references?: MasterDataReference[];
  effectiveFrom: string;
  effectiveTo?: string | null;
  previousVersionId?: string | null;
  changeReason?: string | null;
  retiredAt?: string | null;
  retiredReason?: string | null;
  retiredWithUnverifiableUsage?: boolean;
  createdBy?: string;
  createdAt?: string;
}

/** POST /platform/master-data/:domain — the FIRST version of a code. */
export interface CreateMasterDataValueRequest {
  code: string;
  label: string;
  description?: string;
  payload?: Record<string, unknown>;
  references?: MasterDataReference[];
  publishImmediately?: boolean;
  effectiveFrom?: string;
  changeReason?: string;
}

/** POST /platform/master-data/:domain/:code/versions — every subsequent change. */
export interface PublishMasterDataVersionRequest {
  label: string;
  description?: string;
  payload?: Record<string, unknown>;
  references?: MasterDataReference[];
  effectiveFrom?: string;
  changeReason: string;
}

/** POST /platform/master-data/:domain/:code/status. */
export interface ChangeMasterDataStatusRequest {
  status: MasterDataValueStatus;
  reason: string;
}

/* ─── PA-13 operations and health ─── */

/**
 * How a signal is doing, including the two answers most dashboards refuse to give.
 *
 * NOT_REPORTING and UNKNOWN are not shades of healthy and must never be drawn as
 * green. The first means the measurement exists and nothing has reported to it;
 * the second means this service cannot answer the question at all. The console had
 * no vocabulary for either — it reused IntegrationState, whose members are all
 * assertions — so a snapshot full of holes would have rendered as a clean board.
 */
export const OPERATIONAL_STATES = [
  'HEALTHY',
  'DEGRADED',
  'UNHEALTHY',
  'NOT_REPORTING',
  'UNKNOWN',
] as const;

export type OperationalState = (typeof OPERATIONAL_STATES)[number];

/** States that must never be presented as reassurance. */
export const NON_ASSERTIVE_OPERATIONAL_STATES: readonly OperationalState[] = [
  'NOT_REPORTING',
  'UNKNOWN',
];

export function operationalStateIsReported(state: OperationalState): boolean {
  return !NON_ASSERTIVE_OPERATIONAL_STATES.includes(state);
}

/**
 * One measured thing.
 *
 * `basis` says in words what was counted to reach the verdict, and it is not
 * optional: a verdict with no stated basis is indistinguishable from a guess.
 */
export interface OperationalSignal {
  key: string;
  title: string;
  state: OperationalState;
  basis: string;
  measurements: Record<string, number | string | null>;
  /** Present on NOT_REPORTING and UNKNOWN: what would have to exist to answer. */
  wouldRequire?: string;
  observedAt: string;
}

/**
 * GET /platform/operations/health.
 *
 * `honesty` is the load-bearing field and is rendered, not swallowed: it lists
 * which signals are measured here, which are silent and which cannot be measured
 * at all, so the screen cannot show a full board over a service that instruments
 * a third of itself.
 */
export interface PlatformHealthSnapshot {
  overall: OperationalState;
  signals: OperationalSignal[];
  openIncidents: number;
  activeMaintenanceWindows: number;
  generatedAt: string;
  honesty: {
    measuredHere: string[];
    notReporting: string[];
    notMeasurable: string[];
    statement: string;
  };
}

export const INCIDENT_SEVERITIES = ['CRITICAL', 'MAJOR', 'MINOR'] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

export const INCIDENT_STATUSES = [
  'INVESTIGATING',
  'IDENTIFIED',
  'MITIGATED',
  'RESOLVED',
] as const;

export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

export const OPEN_INCIDENT_STATUSES: readonly IncidentStatus[] = [
  'INVESTIGATING',
  'IDENTIFIED',
  'MITIGATED',
];

export interface PlatformIncident {
  id: string;
  reference: string;
  title: string;
  summary: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  affectedInstitutionIds: string[];
  platformWide: boolean;
  affectedProductCodes: string[];
  detectedAt: string;
  mitigatedAt?: string | null;
  resolvedAt?: string | null;
  resolution?: string | null;
  raisedBy: string;
  resolvedBy?: string | null;
}

export const MAINTENANCE_MODES = ['UNAVAILABLE', 'READ_ONLY'] as const;
export type MaintenanceMode = (typeof MAINTENANCE_MODES)[number];

export interface ProductMaintenanceWindow {
  id: string;
  productCode: string;
  /** Null means every institution — most windows are platform-wide. */
  institutionId: string | null;
  mode: MaintenanceMode;
  startsAt: string;
  endsAt: string;
  userMessage: string;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
  scheduledBy: string;
}

/* ─── PA-16 imports ─── */

/**
 * The lifecycle of one uploaded setup file, mirrored from ImportBatchState.
 *
 * Every one of these is a state somebody is waiting in, and what they are waiting
 * for differs. AWAITING_CORRECTION means the administrator has work to do;
 * PENDING_DOMAIN_VALIDATION means they have none and another service has not
 * answered. The console's old nine-value list collapsed those into "VALIDATING",
 * which is how a batch sits untouched for a week because everyone assumed somebody
 * else was blocked.
 */
export const IMPORT_BATCH_STATES = [
  'RECEIVED',
  'VALIDATING',
  'STRUCTURE_REJECTED',
  'AWAITING_CORRECTION',
  'PENDING_DOMAIN_VALIDATION',
  'READY_FOR_APPROVAL',
  'APPROVED',
  'APPLYING',
  'COMPLETED',
  'FAILED',
  'REJECTED',
  'CANCELLED',
] as const;

export type ImportBatchState = (typeof IMPORT_BATCH_STATES)[number];

export const DOMAIN_VALIDATION_STATUSES = [
  'NOT_REQUESTED',
  'REQUESTED',
  'VERIFIED',
  'REJECTED',
  'UNAVAILABLE',
] as const;

export type DomainValidationStatus = (typeof DOMAIN_VALIDATION_STATUSES)[number];

/**
 * One staged import.
 *
 * `templateCode`, not `dataSet`; `batchReference` is what people quote to each
 * other. There is no `acceptedRows`/`rejectedRows` pair on the batch — those come
 * from the progress endpoint, computed from the rows themselves so they cannot
 * disagree with a counter.
 */
export interface ImportBatch {
  id: string;
  batchReference: string;
  institutionId: string;
  onboardingCaseId?: string | null;
  templateCode: string;
  state: ImportBatchState;
  fileName: string;
  declaredFileHash?: string;
  payloadHash?: string;
  declaredColumns?: string[];
  sourceSystem?: string;
  sourceSystemName?: string | null;
  sourceIdKind?: string | null;
  domainValidationOwner?: string;
  domainValidationStatus: DomainValidationStatus;
  domainValidationNote?: string | null;
  totalRows: number;
  validationAttempt?: number;
  approvedBy?: string | null;
  approvedByName?: string | null;
  /** Non-null is the only proof anything took effect. */
  approvedAt?: string | null;
  closureReason?: string | null;
  uploadedBy: string;
  uploadedByName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** GET /platform/imports/batches/:id/progress — counted from the staged rows. */
export interface ImportProgress {
  batchId: string;
  batchReference: string;
  state: ImportBatchState;
  domainValidationStatus: DomainValidationStatus;
  domainValidationOwner: string;
  totalRows: number;
  pendingValidation: number;
  structureValid: number;
  quarantined: number;
  domainVerified: number;
  imported: number;
  /** Any of these blocks approval. */
  openQuarantine: number;
  waivedRows: number;
  validationAttempt: number;
  approvedAt: string | null;
}

export const QUARANTINE_RESOLUTIONS = ['OPEN', 'CORRECTED', 'WAIVED'] as const;
export type QuarantineResolution = (typeof QUARANTINE_RESOLUTIONS)[number];

/**
 * One rejected row, with the values that produced it.
 *
 * The values travel with the reason so the download can be corrected in place.
 * `rowNumber` is the line in the ORIGINAL file — an error report that says "row 3"
 * when the operator must scroll to line 847 is not usable.
 */
export interface QuarantineReportEntry {
  rowNumber: number;
  reasonCode: string;
  column: string | null;
  detail: string;
  raisedBy: string;
  resolution: QuarantineResolution;
  resolutionReason: string | null;
  rowValues: Record<string, string | number | boolean | null>;
}

export interface QuarantineReport {
  batchReference: string;
  templateCode: string;
  generatedAt: string;
  columns: string[];
  entries: QuarantineReportEntry[];
}

/* ─── PA-18 reports ─── */

/**
 * The twelve reports core actually serves, each at its own GET route.
 *
 * WHY THE INDEX IS BUILT HERE AND NOT FETCHED
 * There is no `GET /platform/reports`, and asking for one would be asking the
 * server to restate something this console already knows: the route set is fixed
 * at compile time, one method per report on PlatformReportController, and a report
 * that does not exist here cannot be opened from a screen that does not link to
 * it. A fetched index would add a request, a failure mode and a second place for
 * the list to drift, and would still not let the console open a report it has no
 * renderer for. What the server does own — the title, the question, the sources,
 * what was and was not counted — arrives in every report's own metadata and is
 * rendered from there, never from this list.
 *
 * WHY THERE IS NO RUN HISTORY
 * Every route here is a GET and nothing on that controller writes. Reports are
 * computed on demand from live tables; there is no run to record and core states
 * that there should never be one. The console therefore does not ask for a run
 * list and does not show a "not available yet" placeholder for it, because that
 * would promise a feature nobody intends to build.
 */
export const PLATFORM_REPORT_CODES = [
  'INSTITUTIONS_BY_STATUS',
  'INSTITUTIONS_BY_LIFECYCLE_STATE',
  'INSTITUTIONS_BY_TYPE',
  'PRODUCT_ADOPTION',
  'FEATURE_ADOPTION',
  'ONBOARDING_DURATION',
  'INCOMPLETE_ONBOARDING',
  'INTEGRATION_HEALTH',
  'FAILED_SYNCHRONISATION',
  'PRODUCT_ENTITLEMENT_CHANGES',
  'SUSPENDED_INSTITUTIONS',
  'LICENCE_STATUS',
] as const;

export type PlatformReportCode = (typeof PLATFORM_REPORT_CODES)[number];

/** Which query parameters a report accepts. Mirrored from its DTO, not guessed. */
export type ReportParameterName = 'from' | 'to' | 'stalledForAtLeastDays' | 'expiringWithinDays';

export interface PlatformReportRoute {
  code: PlatformReportCode;
  /** The path segment under /platform/reports. */
  path: string;
  parameters: readonly ReportParameterName[];
}

export const PLATFORM_REPORT_ROUTES: readonly PlatformReportRoute[] = [
  { code: 'INSTITUTIONS_BY_STATUS', path: 'institutions-by-status', parameters: [] },
  {
    code: 'INSTITUTIONS_BY_LIFECYCLE_STATE',
    path: 'institutions-by-lifecycle-state',
    parameters: [],
  },
  { code: 'INSTITUTIONS_BY_TYPE', path: 'institutions-by-type', parameters: [] },
  { code: 'PRODUCT_ADOPTION', path: 'product-adoption', parameters: [] },
  { code: 'FEATURE_ADOPTION', path: 'feature-adoption', parameters: [] },
  { code: 'ONBOARDING_DURATION', path: 'onboarding-duration', parameters: ['from', 'to'] },
  {
    code: 'INCOMPLETE_ONBOARDING',
    path: 'incomplete-onboarding',
    parameters: ['stalledForAtLeastDays'],
  },
  { code: 'INTEGRATION_HEALTH', path: 'integration-health', parameters: [] },
  { code: 'FAILED_SYNCHRONISATION', path: 'failed-synchronisation', parameters: [] },
  {
    code: 'PRODUCT_ENTITLEMENT_CHANGES',
    path: 'product-entitlement-changes',
    parameters: ['from', 'to'],
  },
  { code: 'SUSPENDED_INSTITUTIONS', path: 'suspended-institutions', parameters: [] },
  { code: 'LICENCE_STATUS', path: 'licence-status', parameters: ['expiringWithinDays'] },
];

export interface ReportSource {
  table: string;
  ownedBy: string;
  contains: string;
}

export interface ReportScope {
  counted: string[];
  notCounted: string[];
  notes: string[];
}

export interface ReportFreshness {
  basis: 'LIVE_PRIMARY_READ' | 'DERIVED_FROM_HISTORY';
  computedAt: string;
  maxStalenessSeconds: number | null;
  explanation: string;
}

export interface ReportWindow {
  from: string;
  to: string;
}

/** A figure this service will not invent, with the reason it cannot produce it. */
export interface UnmeasuredFact {
  fact: string;
  reason: string;
  wouldRequire: string;
}

export interface PlatformReportMetadata {
  reportCode: PlatformReportCode;
  title: string;
  question: string;
  authority: string;
  notAuthoritativeFor: string[];
  authorityDisclaimer: string;
  sources: ReportSource[];
  scope: ReportScope;
  generatedAt: string;
  freshness: ReportFreshness;
  window: ReportWindow | null;
  rowCount: number;
  unmeasured: UnmeasuredFact[];
  warnings: string[];
}

/**
 * A report row, as it arrives.
 *
 * Deliberately an index signature over JSON rather than a union of the twelve row
 * shapes: the table renders whatever columns the server sent, in the order it sent
 * them, so a report that gains a column shows it without a redeploy. Narrowing to
 * twelve interfaces here would put this file in the business of tracking core's
 * reporting schema, and the first thing to break would be a column silently
 * dropped from a table nobody was watching.
 */
export type ReportCellValue = string | number | boolean | null | Record<string, unknown> | unknown[];
export type ReportRow = Record<string, ReportCellValue>;

export interface PlatformReport {
  metadata: PlatformReportMetadata;
  rows: ReportRow[];
}

/** What a report run needs from the operator, if anything. */
export type ReportParameterValues = Partial<Record<ReportParameterName, string>>;

/* ─── Integration credentials held by CampusOne ─── */

/**
 * Which credential a stored row holds. Mirrors ConnectionSecretKind in
 * campusone-core-service; the strings are a wire contract.
 */
export type ConnectionSecretKind = 'API_KEY' | 'HMAC_SECRET' | 'WEBHOOK_SECRET';

/**
 * What the console is allowed to know about a stored credential — never the value.
 *
 * `fingerprint` is twelve hex characters of a SHA-256, there so an operator can confirm
 * that what CampusOne holds is what the issuing console displayed, without either side
 * rendering the secret.
 */
export interface ConnectionSecretStatus {
  kind: ConnectionSecretKind;
  configured: boolean;
  fingerprint: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface PutConnectionSecretRequest {
  kind: ConnectionSecretKind;
  value: string;
  reason?: string;
}

/**
 * INSUFFICIENT_SCOPE is a PASS. It means the API key and signature both verified and only
 * the permission check declined — expected for a client scoped narrowly, and the reason
 * the UI must not render it as a failure.
 */
export type CredentialTestOutcome =
  | 'ACCEPTED'
  | 'REJECTED'
  | 'INSUFFICIENT_SCOPE'
  | 'UNREACHABLE'
  | 'NOT_CONFIGURED';

export interface CredentialTestResult {
  outcome: CredentialTestOutcome;
  httpStatus: number | null;
  responseTimeMs: number | null;
  detail: string;
  credentialSource: 'stored' | 'environment' | 'mixed' | 'none';
}
